import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { usePermissions } from '../../hooks/usePermissions';
import ReviewInterface from './ReviewInterface';
import DecisionView from './DecisionView';
import PreScreenView from './PreScreenView';
import ReviewerContextAdmin from './ReviewerContextAdmin';
import ArchiveSearch from './ArchiveSearch';
import ProducerDashboard from './ProducerDashboard';
import { canSeeTab, defaultTab, resolveTab } from './dashboardTabs';
import type { DashboardTab } from './dashboardTabs';
import DeadlinePill from './DeadlinePill';
import TaxonomySelect from './TaxonomySelect';
import TaxonomyAdmin from './TaxonomyAdmin';
import { useCurrentUserStep } from './useCurrentUserStep';
import MentionedCyclesSection from './MentionedCyclesSection';
import { reviewerStage, waitingOnLabel, currentStep, STEP_LABEL } from './reviewStage';
import type { User, ReviewCycle, CycleStatus } from './types';
import { automationSummary, AUTOMATION_STEP_LABELS } from './constants';
import './ContentReview.css';
import { GuideLink } from '../common';
import { ROLE_GUIDES } from '../../utils';

interface ReviewDashboardProps {
  user: User | null;
}

// Phase 8C — row shape returned by GET /submission-queue
export interface SubmissionQueueFile {
  fileId: string;
  fileName: string;
  size?: number;
  lastModified?: string;
  parsedMeta?: { purpose: string; audience: string; title: string; version: string } | null;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const STATUS_LABELS: Record<CycleStatus, string> = {
  pending_prescreen: 'Awaiting Pre-Screen',
  pending_review: 'Pending Review',
  in_review: 'In Review',
  approved: 'Approved',
  revision_needed: 'Revision Needed',
  rejected: 'Rejected',
  // Phase 8E — admin canceled state.
  canceled: 'Canceled',
  superseded: 'Superseded'
};

// Format = the FILE family (auto-detected contentType), technical names so no
// word sits on both the Type and Format axes (Fredrik 2026-09-08). Keep in
// step with ArchiveSearch.
const CONTENT_TYPE_LABELS: Record<string, string> = {
  docx: 'Word',
  pptx: 'PowerPoint',
  pdf: 'PDF',
  image: 'Image file',
  video: 'Video file',
  webpage: 'Web page'
};

const ReviewDashboard: React.FC<ReviewDashboardProps> = ({ user }) => {
  const { instance } = useMsal();
  const { hasSubcategoryAccess, isAdmin: isAdminFn } = usePermissions(user);
  const canMakeDecisions = hasSubcategoryAccess('canMakeReviewDecisions');
  const canManageSubmissions = hasSubcategoryAccess('canManageReviewSubmissions');
  const canSubmitContent = hasSubcategoryAccess('canSubmitContent');
  const canReviewContent = hasSubcategoryAccess('canReviewContent');
  // Mentioned participant (D69, 2026-08-21): no reviewer/DM grant, but
  // @mentioned on an in-flight cycle. The backend scopes GET /cycles to
  // those cycles and allows reply/consultation only; this flag drives the
  // read-only presentation.
  const isParticipant = !canReviewContent && !canMakeDecisions && hasSubcategoryAccess('isReviewParticipant');

  // Which tabs can this person actually USE? (Fredrik, 2026-08-24, walking as
  // the producer.) Both of these used to render for everyone:
  //
  //   Review Queue — GET /cycles is reviewer / DM / participant only, so a pure
  //     producer got a 403 into the error banner on every dashboard load and a
  //     tab that could never hold anything.
  //   Archive      — was the same 403 (a second dead tab); re-opened 2026-09-02
  //     to the whole module — see dashboardTabs.ts.
  //
  // A producer's home is My Submissions (Fredrik's call, 2026-08-21). Showing
  // them a queue they cannot read is the same defect as the button they could
  // not reach: the UI describing a path that is not theirs.
  const tabCaps = {
    canReviewContent, canMakeDecisions, canManageSubmissions,
    canSubmitContent, isParticipant
  };
  const canSeeQueue = canSeeTab('queue', tabCaps);
  const canSeeArchive = canSeeTab('archive', tabCaps);

  const [reviews, setReviews] = useState<ReviewCycle[]>([]);
  // v2 (D90) — slug→label maps for the slug-stored dimensions, so chips
  // and detail rows render display labels while cycles store slugs.
  const [dimLabels, setDimLabels] = useState<Record<string, string>>({});
  // Bumped whenever a taxonomy option may have been added or relabelled, so the
  // slug→label map is re-read. Without this the map was fetched once with []
  // deps and a topic added inline at submit, or renamed in the Registries tab,
  // rendered as its raw slug on every row until a page reload (2026-08-21 audit).
  const [taxonomyVersion, setTaxonomyVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(() => {
    // Deep link from a mention / thread-reply email (?cycle=<id>), and — since
    // 2026-08-26 — the address bar of any open cycle, kept in sync by the
    // effect below.
    try { return new URLSearchParams(window.location.search).get('cycle') || null; } catch { return null; }
  });
  // Phase 8B — added 'my-submissions' for producers (D4). Visible only when
  // canSubmitContent (mirrors backend producer-dashboard gate).
  // Default to the first tab this user can actually use — 'queue' would land a
  // producer on an empty list behind a 403 before they had clicked anything.
  const [activeTab, setActiveTab] = useState<DashboardTab>(() => defaultTab(tabCaps));
  const shownTab = resolveTab(activeTab, tabCaps);

  // Phase 8C — submit-content flow state. Chooser opens picker → either web
  // (existing SubmitForReviewModal) or file (new SubmissionQueueModal →
  // SubmitFileMetadataModal). Single flag drives all four states; the modals
  // close it back to null on cancel/success.
  const [submitFlow, setSubmitFlow] = useState<null | 'chooser' | 'web' | 'file-queue' | { kind: 'file-form', file: SubmissionQueueFile }>(null);
  // Legacy "Submit Web Pages" button still wires to setShowSubmitModal so
  // the existing modal is intact; producers use the new chooser instead.
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [flashMessage, setFlashMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  // Fredrik 2026-08-12 (UI walk) — free-text queue search. The queue can
  // hold hundreds of cycles; scrolling to find one by name doesn't scale.
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusSubFilter, setStatusSubFilter] = useState<string>('');
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('');
  // Which ROLE'S page to render for the open cycle — 2026-08-31 (Fredrik:
  // "for admins I'd like Admin view, DM view and Reviewer view so that I can
  // see what each role sees"). Was a boolean reviewer-view toggle.
  //
  // SIMULATES THE PAGE, NOT THE PERMISSIONS. The backend auto-grants admins
  // on every check, so a control that renders in Reviewer view may still
  // succeed for an admin where a real reviewer would be refused. What it
  // faithfully reproduces is what each role finds on the page — which
  // controls exist and where — and DM view drops the admin-only extras
  // (Delete; unlink still follows manage_submissions like a real DM).
  const [viewRole, setViewRole] = useState<'admin' | 'dm' | 'reviewer' | 'producer'>('admin');
  const isAdmin = isAdminFn();

  const [editingMetadata, setEditingMetadata] = useState<string | null>(null);
  // Format of the cycle open in the metadata editor — drives the channel
  // dropdown's allowedFormats filtering (walk fix F4).
  const [metaFormat, setMetaFormat] = useState<string | null>(null);
  // 14e-A walk station 4 — sourceLink + noEditableSourceReason join the
  // edit dialog (late-attach/replace; PUT /metadata re-runs the D81 gate
  // on the merged result server-side).
  // sourceFileId/sourceFileName are nullable here on purpose: the PUT treats a
  // null as "clear" and a value as "replace", and they must move as a pair.
  // Producer reassignment from the edit modal (2026-08-19, Fredrik). Kept
  // SEPARATE from metaForm because it is a different endpoint:
  // PATCH /cycles/:id/producer already exists (Phase 8D) and appends to
  // producerHistory with a status guard. Folding producer into the metadata
  // PUT would create a second write path that bypasses both — the audit trail
  // and the guard are the reason the dedicated endpoint exists. Until now
  // nothing in the UI called it at all.
  const [metaTeam, setMetaTeam] = useState<TeamMemberLite[]>([]);
  const [metaProducerId, setMetaProducerId] = useState<string>('');
  const [metaProducerOriginal, setMetaProducerOriginal] = useState<string>('');
  const [metaStatus, setMetaStatus] = useState<string>('');
  const [metaForm, setMetaForm] = useState<{
    name: string; publicName: string; type: string; audience: string; channel: string;
    topic: string; geography: string; sourceLink: string; noEditableSourceReason: string;
    sourceFileId: string | null; sourceFileName: string | null;
  }>({ name: '', publicName: '', type: '', audience: '', channel: '', topic: '', geography: '', sourceLink: '', noEditableSourceReason: '', sourceFileId: null, sourceFileName: null });

  // Smart Import queue removed in Phase K (2026-05-08). Producers now use
  // the universal submit-content flow (Phase 8C) which handles badly-named
  // files via SubmitFileMetadataModal. See CONTENT_REVIEW_SYSTEM.md → D1.

  // Helper: changing top-level filter resets sub-filters
  const applyStatusFilter = (filter: string) => {
    setStatusFilter(filter);
    setStatusSubFilter('');
    setContentTypeFilter('');
  };
  const [linkingParent, setLinkingParent] = useState<string | null>(null);
  const [selectedForDelete, setSelectedForDelete] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    if (!user) throw new Error('User not authenticated');

    if (user.isExternal) {
      const token = user.token || sessionStorage.getItem('externalUserToken');
      return {
        'Authorization': `Bearer ${token}`,
        'x-user-email': user.email,
        'x-user-name': user.name,
        'x-external-user': 'true'
      };
    }

    const response = await instance.acquireTokenSilent({
      scopes: ['User.Read', 'Sites.ReadWrite.All', 'Files.ReadWrite.All'],
      account: user.account
    });
    return {
      'Authorization': `Bearer ${response.accessToken}`,
      'x-user-email': user.email,
      'x-user-name': user.name
    };
  }, [user, instance]);

  // 2026-08-26 (Fredrik) — who may open the metadata editor.
  //
  // Backend gate is requireMetadataEditAccess: decision_maker OR
  // manage_submissions on any cycle, OR the producer on their own. The button
  // was gated on canManageSubmissions alone, which produced BOTH halves of the
  // mismatch: Johanna (manage_submissions, no DM) could see the button and was
  // refused by the old DM-only backend; and a producer without
  // manage_submissions is now allowed by the backend but would still not see
  // the control. That second half is the "access grant hides the control"
  // shape this codebase has hit repeatedly — the permission widens, the
  // read-only branch keeps the button hidden, and nobody can use the grant.
  //
  // canSubmitContent stands in for "holds the producer bit" (it is
  // producer || manage_submissions || decision_maker — every holder of which
  // the backend already admits), so it only ever narrows this to someone the
  // server would accept anyway.
  const canEditMetadata = useCallback((review: ReviewCycle) => {
    if (canManageSubmissions || canMakeDecisions) return true;
    const mine = (review.producer?.email || '').toLowerCase() === (user?.email || '').toLowerCase();
    return canSubmitContent && !!review.producer?.email && mine;
  }, [canManageSubmissions, canMakeDecisions, canSubmitContent, user]);

  // Phase 12B — resolve which sequenced-review step the current user acts
  // at. Still used by the sequence indicator and by step completion; the
  // edit-lock panel it was originally added for was removed 2026-08-21.
  const currentUserStep = useCurrentUserStep(user, {
    isProducer: canSubmitContent,
    isDecisionMaker: canMakeDecisions,
    getAuthHeaders
  });

  const fetchReviews = useCallback(async () => {
    if (!user) return;
    // A producer cannot read this list at all. Asking anyway put "Content
    // review access required" on their dashboard on every load.
    if (!canSeeQueue) { setReviews([]); setLoading(false); return; }
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const res = await axios.get(`${backendUrl}/api/content-review/cycles`, { headers });
      let cycles: ReviewCycle[] = res.data.filter((r: ReviewCycle) => !r.parentReview);

      // Reviewers only see items assigned to them. Participants' lists are
      // already scoped server-side to the cycles they are mentioned on.
      if (!canMakeDecisions && !isParticipant && user.email) {
        cycles = cycles.filter(r =>
          r.assignedReviewers?.some(rev => rev.email === user.email)
        );
      }

      setReviews(cycles);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders, canMakeDecisions, isParticipant, canSeeQueue]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  // v2 (D90) — one fetch of the slug-valued kinds for display resolution.
  // Includes inactive rows: a retired slug on an old cycle must still
  // render its label rather than raw slug.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const [topics, geos] = await Promise.all([
          axios.get(`${backendUrl}/api/content-review/taxonomy?kind=topic&includeInactive=1`, { headers }),
          axios.get(`${backendUrl}/api/content-review/taxonomy?kind=geography&includeInactive=1`, { headers })
        ]);
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const o of [...(topics.data || []), ...(geos.data || [])]) {
          if (o.slug) map[o.slug] = o.label;
        }
        setDimLabels(map);
      } catch { /* labels degrade to raw slugs */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxonomyVersion, activeTab]);

  // 2026-08-26 (Fredrik) — mirror the open cycle into the address bar.
  //
  // The id was previously readable ONLY on the way in, from a mention email's
  // ?cycle= deep link. Opening a cycle from the queue left the URL on the bare
  // dashboard, so the id — which every backend utility takes as its argument
  // (inspectCycles --id=…) and which the UI shows nowhere else — could not be
  // got at without a DB read.
  //
  // replaceState, not pushState: the dashboard has no popstate listener, so a
  // history entry per selection would let Back change the URL while the view
  // stayed put — a worse bug than the one being fixed. This writes the address
  // bar and touches nothing else.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (selectedReviewId) url.searchParams.set('cycle', selectedReviewId);
      else url.searchParams.delete('cycle');
      if (url.href !== window.location.href) window.history.replaceState(null, '', url.href);
    } catch {
      // Non-fatal by design: a URL the browser won't parse must not take the
      // dashboard down with it. The id stays visible in the app either way.
    }
  }, [selectedReviewId]);

  // `flash` carries a one-line confirmation back from the review view. The
  // reviewer is redirected here on submit (2026-08-20, Fredrik), and a
  // redirect with no explanation reads as "something went wrong" — the
  // previous behaviour was to stay put, where the submitted banner was the
  // confirmation.
  const handleBack = (flash?: string) => {
    setSelectedReviewId(null);
    setViewRole('admin');
    // Only a string may become flash state. A child that wires this to
    // onClick directly hands us the click EVENT, and rendering an object as a
    // React child throws #31, which unmounts the entire app — the white page
    // Fredrik hit on "Back to Dashboard" during the 2026-08-21 prod E2E. The
    // call sites are fixed and now typed to catch it, but this guard is what
    // makes the failure impossible rather than merely absent today.
    setFlashMessage(typeof flash === 'string' ? flash : null);
    fetchReviews();
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!window.confirm('Are you sure you want to delete this review? This cannot be undone.')) return;
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${backendUrl}/api/content-review/cycles/${reviewId}`, { headers });
      handleBack();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete review');
    }
  };

  const handleLinkAsChild = async (childId: string, parentId: string) => {
    try {
      const headers = await getAuthHeaders();
      await axios.put(`${backendUrl}/api/content-review/cycles/${childId}/parent`, {
        parentId
      }, { headers });
      setLinkingParent(null);
      fetchReviews();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to link reviews');
    }
  };

  const toggleSelectForDelete = (id: string) => {
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Removed in Phase K (2026-05-08) — handleImportFile / handleImportAll /
  // updateImportItem were the Smart Import queue handlers. The /import-file
  // backend route is also gone; producers route through /submit-file via
  // SubmitFileMetadataModal. See CONTENT_REVIEW_SYSTEM.md → D1.

  const openMetadataEditor = (review: ReviewCycle, e: React.MouseEvent) => {
    e.stopPropagation();
    setMetaForm({
      name: review.contentItem.name,
      publicName: (review.contentItem as any).publicName || '',
      type: review.contentItem.type || '',
      audience: review.contentItem.audience || '',
      // WS2 walk finding #1 (2026-08-11) — edit mirrors creation: Channel
      // is editable; changing it re-routes postApprovalAction server-side.
      channel: review.channel || '',
      // 14e-A — current source state loads into the dialog for
      // late-attach/replace. sourceFileId isn't editable here (companion
      // file replacement is a resubmit concern); link + reason are.
      sourceLink: (review.contentItem as any).sourceLink || '',
      noEditableSourceReason: (review.contentItem as any).noEditableSourceReason || '',
      // 2026-08-18 — the companion IS detachable here now. The comment above
      // used to call replacement "a resubmit concern", but the PUT has always
      // accepted a paired null, and with no UI a mis-attached companion was a
      // one-way door: it silently becomes the archived working master, and
      // resubmitting means deleting a cycle that may no longer be deletable.
      sourceFileId: (review.contentItem as any).sourceFileId || null,
      sourceFileName: (review.contentItem as any).sourceFileName || null,
      // v2 (D90) — legacy cycles render under the same fallbacks lineage
      // matching uses, so what the admin sees is what matching does.
      topic: (review.contentItem as any).topic || 'other',
      geography: (review.contentItem as any).geography || 'global'
    });
    // Walk fix F4 (2026-08-12) — the channel select in the edit modal
    // filters by the cycle's stored format, mirroring the submit form
    // (the server backstop rejects incompatible re-routes regardless).
    setMetaFormat((review as any).contentType || null);
    const currentProducerId = (review as any).producer?.teamMemberId || '';
    setMetaProducerId(currentProducerId);
    setMetaProducerOriginal(currentProducerId);
    setMetaStatus((review as any).status || '');
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/team`, { headers });
        setMetaTeam((res.data || []).map((m: any) => ({ _id: m._id, name: m.name, email: m.email })));
      } catch (err) {
        console.error('Error loading team for producer reassignment:', err);
        setMetaTeam([]);
      }
    })();
    setEditingMetadata(review._id);
  };

  const handleSaveMetadata = async () => {
    if (!editingMetadata) return;
    try {
      const headers = await getAuthHeaders();
      await axios.put(`${backendUrl}/api/content-review/cycles/${editingMetadata}/metadata`, metaForm, { headers });
      // Only when it actually changed — a no-op PATCH would still append a
      // producerHistory entry, filling the audit trail with reassignments that
      // reassigned nothing.
      if (metaProducerId && metaProducerId !== metaProducerOriginal) {
        await axios.patch(
          `${backendUrl}/api/content-review/cycles/${editingMetadata}/producer`,
          { producer: { teamMemberId: metaProducerId } },
          { headers }
        );
      }
      setEditingMetadata(null);
      fetchReviews();
      // The edit modal can add a topic/geography inline too.
      setTaxonomyVersion(v => v + 1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update metadata');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedForDelete.size === 0) return;
    if (!window.confirm(`Delete ${selectedForDelete.size} selected item${selectedForDelete.size !== 1 ? 's' : ''}? This cannot be undone.`)) return;

    try {
      setDeleting(true);
      const headers = await getAuthHeaders();
      const ids = Array.from(selectedForDelete);
      for (const id of ids) {
        await axios.delete(`${backendUrl}/api/content-review/cycles/${id}`, { headers });
      }
      setSelectedForDelete(new Set());
      fetchReviews();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete items');
    } finally {
      setDeleting(false);
    }
  };

  const selectAll = () => {
    if (selectedForDelete.size === reviews.length) {
      setSelectedForDelete(new Set());
    } else {
      setSelectedForDelete(new Set(reviews.map(r => r._id)));
    }
  };

  // Detail view routing
  if (selectedReviewId) {
    const review = reviews.find(r => r._id === selectedReviewId);

    if (canMakeDecisions && review?.status === 'pending_prescreen') {
      return (
        <PreScreenView
          user={user}
          cycleId={selectedReviewId}
          getAuthHeaders={getAuthHeaders}
          onBack={handleBack}
          canUnlinkLineage={canManageSubmissions}
          // Phase 8E gap fix — Cancel available at pre-screen too. Mirrors
          // the DecisionView permission gate below.
          canCancelCycles={canMakeDecisions || canManageSubmissions}
          canDeleteChildCycles={canMakeDecisions}
          // Phase 12B — edit-lock panel props.
          currentUserStep={currentUserStep}
          canEditAnyComment={canMakeDecisions}
        />
      );
    }

    if (canMakeDecisions && viewRole !== 'reviewer' && viewRole !== 'producer') {
      const dmSimulated = isAdmin && viewRole === 'dm';
      return (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
            {isAdmin ? (
              <div data-testid="role-view-switch" style={{ display: 'flex', gap: 4 }}>
                {(['admin', 'dm', 'reviewer', 'producer'] as const).map(r => (
                  <button
                    key={r}
                    className={`review-btn ${viewRole === r ? '' : 'review-btn-outline'}`}
                    onClick={() => setViewRole(r)}
                    style={{ fontSize: 12, padding: '6px 12px' }}
                    title={r === 'admin' ? 'Everything, including admin-only controls'
                      : r === 'dm' ? "The decision maker's page, without admin extras"
                      : r === 'reviewer' ? "The reviewer's page for this cycle"
                      : "The producer's page: read-back panel, reply-only composer, trimmed comments"}
                  >
                    {r === 'admin' ? 'Admin view' : r === 'dm' ? 'DM view' : r === 'reviewer' ? 'Reviewer view' : 'Producer view'}
                  </button>
                ))}
              </div>
            ) : (
              <button
                className="review-btn review-btn-outline"
                onClick={() => setViewRole('reviewer')}
                style={{ fontSize: 12, padding: '6px 12px' }}
              >
                Switch to Reviewer View
              </button>
            )}
            {!dmSimulated && (
              <button
                className="review-btn review-btn-danger"
                onClick={() => handleDeleteReview(selectedReviewId)}
                style={{ fontSize: 12, padding: '6px 12px' }}
              >
                Delete
              </button>
            )}
          </div>
          <DecisionView
            user={user}
            cycleId={selectedReviewId}
            getAuthHeaders={getAuthHeaders}
            onBack={handleBack}
            // Phase 8E — surface Cancel cycle button when user has the
            // backend permissions (mirrors backend route gate).
            canCancelCycles={canMakeDecisions || canManageSubmissions}
            canDeleteChildCycles={canMakeDecisions}
            // Phase 8C remainder — Unlink lineage gated on manage_submissions
            // (admin role inherits). DM alone is NOT enough per D3.
            // In simulated DM view, unlink renders as it would for a plain
            // decision maker WITHOUT manage_submissions (D3: DM alone is not
            // enough) — the admin auto-grant would otherwise leak through the
            // simulation.
            canUnlinkLineage={dmSimulated ? false : canManageSubmissions}
            // Phase 12B — edit-lock panel props.
            currentUserStep={currentUserStep}
            canEditAnyComment={canMakeDecisions}
            // O-2 — mirrors requireDecisionMaker on the re-run route.
            canRerun={canMakeDecisions}
          />
        </div>
      );
    }

    return (
      <div>
        {canMakeDecisions && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 4, justifyContent: 'flex-end' }}>
            <button
              className="review-btn review-btn-outline"
              onClick={() => setViewRole(isAdmin ? 'admin' : 'dm')}
              style={{ fontSize: 12, padding: '6px 12px' }}
            >
              {isAdmin ? 'Back to Admin view' : 'Switch to Decision View'}
            </button>
          </div>
        )}
        <ReviewInterface
          user={user}
          cycleId={selectedReviewId}
          getAuthHeaders={getAuthHeaders}
          onBack={handleBack}
          // Phase 12 fix pass Batch B — same shape as DecisionView props.
          currentUserStep={currentUserStep}
          participant={isParticipant}
          // 2026-09-01 — Producer view (Fredrik): forces the producer's page
          // shape even though the admin is not the cycle's named producer.
          // Same stated limit as the rest of the switcher: it simulates the
          // PAGE, not the permissions.
          simulateProducer={isAdmin && viewRole === 'producer'}
          // Reviewer/producer views render THAT role's page: neither may edit
          // other people's comments, and showing the admin's power here is
          // exactly what the simulation exists to avoid. (Backend calls still
          // succeed for an admin — the switch simulates the page, not the
          // permissions.)
          canEditAnyComment={canMakeDecisions && viewRole !== 'reviewer' && viewRole !== 'producer'}
        />
      </div>
    );
  }

  // Stats — role-aware.
  //
  // 2026-08-20 (Fredrik) — this used to ask only "did I submit?", so every
  // unsubmitted cycle read "Needs Your Review" regardless of whose turn it
  // was. A reviewer third in the sequence was told to act on a cycle they
  // could not touch: the edit lock is step-gated, so the control they'd
  // reach for wasn't there, with nothing explaining why. Now it splits by
  // sequence position, so a reviewer can tell "I am holding this up" from
  // "I am waiting on Medical".
  const getReviewerStatus = (r: ReviewCycle) => reviewerStage(r, user?.email, currentUserStep);

  // For reviewers
  const yourTurn = !canMakeDecisions
    ? reviews.filter(r => getReviewerStatus(r) === 'your_turn').length : 0;
  const waitingOnOthers = !canMakeDecisions
    ? reviews.filter(r => getReviewerStatus(r) === 'waiting').length : 0;
  const submitted = !canMakeDecisions
    ? reviews.filter(r => getReviewerStatus(r) === 'submitted').length : 0;

  // "Approved and HELD — nothing has shipped" is the decision maker's work,
  // not a result (2026-08-31, mid-walk: the first-ever held cycle filed under
  // Approved, which reads as done, and Confirm fixes was nowhere). The
  // producer dashboard learned this on 2026-08-28 (Q3, producerTabs.js); this
  // dashboard never did — the same one-predicate-two-copies drift, one level
  // up. One helper here so the count, the filter, the sub-chip and the row
  // highlight cannot disagree.
  const isHeldOnFixes = (r: ReviewCycle) => r.status === 'approved' && r.pendingFixes === true;

  // For decision makers
  const actionNeeded = canMakeDecisions
    ? reviews.filter(r => r.status === 'pending_prescreen' || (r.status === 'in_review' && r.allReviewersComplete) || isHeldOnFixes(r)).length : 0;
  const inReview = canMakeDecisions
    ? reviews.filter(r => r.status === 'pending_review' || (r.status === 'in_review' && !r.allReviewersComplete)).length : 0;

  // Shared. Approved excludes held-on-fixes for a DM — those are counted as
  // action needed above; for reviewers the plain status keeps its meaning.
  const approved = reviews.filter(r => r.status === 'approved' && !(canMakeDecisions && isHeldOnFixes(r))).length;
  const needsRevision = reviews.filter(r => r.status === 'revision_needed' || r.status === 'rejected').length;

  // Apply local filter — role-aware
  const filteredReviews = (() => {
    if (statusFilter === '') return reviews;
    if (!canMakeDecisions) {
      // Reviewer filters
      if (statusFilter === 'your_turn') return reviews.filter(r => getReviewerStatus(r) === 'your_turn');
      if (statusFilter === 'waiting') return reviews.filter(r => getReviewerStatus(r) === 'waiting');
      if (statusFilter === 'submitted') return reviews.filter(r => getReviewerStatus(r) === 'submitted');
      if (statusFilter === 'approved') return reviews.filter(r => r.status === 'approved');
      if (statusFilter === 'revision_needed') return reviews.filter(r => r.status === 'revision_needed' || r.status === 'rejected');
      return reviews;
    }
    // DM filters
    if (statusFilter === 'action_needed') return reviews.filter(r => r.status === 'pending_prescreen' || (r.status === 'in_review' && r.allReviewersComplete) || isHeldOnFixes(r));
    if (statusFilter === 'in_review') return reviews.filter(r => r.status === 'pending_review' || (r.status === 'in_review' && !r.allReviewersComplete));
    if (statusFilter === 'approved') return reviews.filter(r => r.status === 'approved' && !isHeldOnFixes(r));
    if (statusFilter === 'revision_needed') return reviews.filter(r => r.status === 'revision_needed' || r.status === 'rejected');
    return reviews.filter(r => r.status === statusFilter);
  })();

  // Compute sub-status labels and filters for the current top-level filter
  type SubStatus = { key: string; label: string; filter: (r: ReviewCycle) => boolean };
  const subStatuses: SubStatus[] = (() => {
    if (canMakeDecisions) {
      if (statusFilter === 'action_needed') return [
        { key: 'prescreen', label: 'Pre-Screen', filter: (r: ReviewCycle) => r.status === 'pending_prescreen' },
        { key: 'ready_decision', label: 'Ready for Decision', filter: (r: ReviewCycle) => r.status === 'in_review' && r.allReviewersComplete },
        { key: 'pending_fixes', label: 'Awaiting Fixes', filter: isHeldOnFixes },
      ];
      if (statusFilter === 'in_review') return [
        { key: 'pending_review', label: 'Pending Review', filter: (r: ReviewCycle) => r.status === 'pending_review' },
        { key: 'reviewing', label: 'In Review', filter: (r: ReviewCycle) => r.status === 'in_review' && !r.allReviewersComplete },
      ];
      if (statusFilter === 'revision_needed') return [
        { key: 'revision', label: 'Revision Needed', filter: (r: ReviewCycle) => r.status === 'revision_needed' },
        { key: 'rejected', label: 'Rejected', filter: (r: ReviewCycle) => r.status === 'rejected' },
      ];
    }
    return [];
  })();

  // Apply status sub-filter
  const subFilteredReviews = statusSubFilter
    ? filteredReviews.filter(subStatuses.find(s => s.key === statusSubFilter)?.filter || (() => true))
    : filteredReviews;

  // Apply content type sub-filter
  const finalFilteredReviews = contentTypeFilter
    ? subFilteredReviews.filter(r => r.contentType === contentTypeFilter)
    : subFilteredReviews;

  // Free-text search on top of the structured filters — matches title,
  // public name, producer, channel, and type.
  const q = searchQuery.trim().toLowerCase();
  const visibleReviews = q
    ? finalFilteredReviews.filter(r =>
        [
          r.contentItem?.name,
          (r.contentItem as any)?.publicName,
          r.producer?.name,
          r.channel,
          r.contentItem?.type
        ].some(v => (v || '').toLowerCase().includes(q))
      )
    : finalFilteredReviews;

  // Compute sub-status counts
  const subStatusCounts: Record<string, number> = {};
  subStatuses.forEach(s => {
    subStatusCounts[s.key] = filteredReviews.filter(s.filter).length;
  });

  // Compute content type counts from the sub-filtered list
  const contentTypeCounts: Record<string, number> = {};
  subFilteredReviews.forEach(r => {
    const ct = r.contentType || 'unknown';
    contentTypeCounts[ct] = (contentTypeCounts[ct] || 0) + 1;
  });

  return (
    <div className="content-review">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Content Review</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* "Check SharePoint" removed 2026-09-01 (Fredrik): a stray click
              mass-imported the Ready folder into cycles. The backend route
              (POST /check-files) still exists if we rebuild the affordance. */}
          {/* My Submissions has its own "+ Submit content" (ProducerDashboard).
              Both have always existed; they only started colliding on
              2026-08-24, when producers began defaulting to that tab instead
              of landing on the queue. Keep the header button for the tabs that
              have no submit control of their own. */}
          {canSubmitContent && shownTab !== 'my-submissions' && (
            <button className="review-btn review-btn-primary" onClick={() => setSubmitFlow('chooser')}>
              + Submit Content
            </button>
          )}
        </div>
      </div>

      {/* Phase 12F — cycles where the current user is @mentioned. Silent
          when there are no active mentions; renders above filters + list
          so it's front-and-center when it does have entries. */}
      <MentionedCyclesSection
        getAuthHeaders={getAuthHeaders}
        onOpenCycle={id => setSelectedReviewId(id)}
      />

      {/* Smart Import queue removed in Phase K (2026-05-08). Badly-named
          SharePoint files are now surfaced through the producer submit-content
          flow (My Submissions → Submission Queue tab). */}

      {/* Tabs */}
      <div className="review-tabs">
        {canSeeQueue && (
          <button
            className={`review-tab ${shownTab === 'queue' ? 'active' : ''}`}
            onClick={() => setActiveTab('queue')}
          >
            Review Queue
          </button>
        )}
        {canSubmitContent && (
          <button
            className={`review-tab ${shownTab === 'my-submissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-submissions')}
            data-testid="tab-my-submissions"
          >
            My Submissions
          </button>
        )}
        {canSeeArchive && (
          <button
            className={`review-tab ${shownTab === 'archive' ? 'active' : ''}`}
            onClick={() => setActiveTab('archive')}
          >
            Archive
          </button>
        )}
        {canMakeDecisions && (
          <button
            className={`review-tab ${shownTab === 'contexts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contexts')}
          >
            Reviewer Contexts
          </button>
        )}
        {/* WS2 (D75) — taxonomy admin (rename / deactivate / re-map channels).
            Label "Registries" per Fredrik (UI walk 2026-08-12); internal
            tab key + testid unchanged. */}
        {(canMakeDecisions || canManageSubmissions) && (
          <button
            className={`review-tab ${shownTab === 'taxonomy' ? 'active' : ''}`}
            onClick={() => setActiveTab('taxonomy')}
            data-testid="tab-taxonomy"
          >
            Registries
          </button>
        )}
      </div>

      {shownTab === 'contexts' ? (
        <ReviewerContextAdmin user={user} getAuthHeaders={getAuthHeaders} />
      ) : shownTab === 'taxonomy' ? (
        <TaxonomyAdmin
          getAuthHeaders={getAuthHeaders}
          // Folder-popup items open the cycle's detail view directly; the
          // selectedReviewId routing takes over regardless of active tab,
          // and Back returns here.
          onOpenCycle={id => setSelectedReviewId(id)}
        />
      ) : shownTab === 'archive' ? (
        <ArchiveSearch user={user} getAuthHeaders={getAuthHeaders} onOpenCycle={id => setSelectedReviewId(id)} />
      ) : shownTab === 'my-submissions' ? (
        <ProducerDashboard
          user={user}
          getAuthHeaders={getAuthHeaders}
          // Returning from the cycle must land where they came from. This used
          // to switch to 'queue' unconditionally, which for a producer is a tab
          // that is now hidden and was always empty.
          onOpenCycle={(id) => { setSelectedReviewId(id); if (canSeeQueue) setActiveTab('queue'); }}
        />
      ) : (
        <>
          {/* Stats — clickable filters, role-aware */}
          {flashMessage && (
            <div
              role="status"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#d4edda', color: '#155724', border: '1px solid #b7dfc3',
                padding: '12px 14px', borderRadius: 8, marginBottom: 16
              }}
            >
              <span style={{ fontSize: 18 }}>&#10003;</span>
              <strong style={{ flex: 1 }}>{flashMessage}</strong>
              <button
                onClick={() => setFlashMessage(null)}
                aria-label="Dismiss"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 18, color: '#155724', lineHeight: 1
                }}
              >
                &times;
              </button>
            </div>
          )}
          <div className="review-stats">
            <div
              className={`review-stat-card ${statusFilter === '' ? 'stat-active' : ''}`}
              onClick={() => applyStatusFilter('')}
              style={{ cursor: 'pointer' }}
            >
              <div className="review-stat-number">{reviews.length}</div>
              <div className="review-stat-label">All</div>
            </div>

            {/* Reviewer-specific stats */}
            {!canMakeDecisions && (
              <>
                {/* Always rendered, including at zero (2026-08-20). The card it
                    replaced was gated on count > 0, so a reviewer with nothing
                    waiting on them saw no such card at all — and could not tell
                    "nothing needs me" from "this dashboard has no idea whose turn
                    it is". Your Turn is the reviewer's primary call to action, so
                    it is the one bucket that has to be a stable anchor. Waiting on
                    Others stays conditional: it is context, not a call to action. */}
                <div
                  className={`review-stat-card ${statusFilter === 'your_turn' ? 'stat-active' : ''}`}
                  onClick={() => applyStatusFilter('your_turn')}
                  style={{
                    cursor: 'pointer',
                    borderLeft: `4px solid ${yourTurn > 0 ? '#f0ad4e' : '#dee2e6'}`,
                    opacity: yourTurn > 0 ? 1 : 0.7
                  }}
                  title={yourTurn > 0
                    ? 'Cycles the sequence is waiting on you for'
                    : 'Nothing is waiting on you right now'}
                >
                  <div className="review-stat-number">{yourTurn}</div>
                  <div className="review-stat-label">Your Turn</div>
                </div>
                {waitingOnOthers > 0 && (
                  <div
                    className={`review-stat-card ${statusFilter === 'waiting' ? 'stat-active' : ''}`}
                    onClick={() => applyStatusFilter('waiting')}
                    style={{ cursor: 'pointer', borderLeft: '4px solid #adb5bd' }}
                    title="Assigned to you, but the sequence is at an earlier reviewer's step"
                  >
                    <div className="review-stat-number">{waitingOnOthers}</div>
                    <div className="review-stat-label">Waiting on Others</div>
                  </div>
                )}
                {submitted > 0 && (
                  <div
                    className={`review-stat-card ${statusFilter === 'submitted' ? 'stat-active' : ''}`}
                    onClick={() => applyStatusFilter('submitted')}
                    style={{ cursor: 'pointer', borderLeft: '4px solid #3730a3' }}
                  >
                    <div className="review-stat-number">{submitted}</div>
                    <div className="review-stat-label">Submitted</div>
                  </div>
                )}
              </>
            )}

            {/* Decision maker stats */}
            {canMakeDecisions && (
              <>
                {actionNeeded > 0 && (
                  <div
                    className={`review-stat-card ${statusFilter === 'action_needed' ? 'stat-active' : ''}`}
                    onClick={() => applyStatusFilter('action_needed')}
                    style={{ cursor: 'pointer', borderLeft: '4px solid #f0ad4e' }}
                  >
                    <div className="review-stat-number">{actionNeeded}</div>
                    <div className="review-stat-label">Action Needed</div>
                  </div>
                )}
                <div
                  className={`review-stat-card ${statusFilter === 'in_review' ? 'stat-active' : ''}`}
                  onClick={() => applyStatusFilter('in_review')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="review-stat-number">{inReview}</div>
                  <div className="review-stat-label">In Review</div>
                </div>
              </>
            )}

            {/* Shared stats */}
            <div
              className={`review-stat-card ${statusFilter === 'approved' ? 'stat-active' : ''}`}
              onClick={() => applyStatusFilter('approved')}
              style={{ cursor: 'pointer' }}
            >
              <div className="review-stat-number">{approved}</div>
              <div className="review-stat-label">Approved</div>
            </div>
            {needsRevision > 0 && (
              <div
                className={`review-stat-card ${statusFilter === 'revision_needed' ? 'stat-active' : ''}`}
                onClick={() => applyStatusFilter('revision_needed')}
                style={{ cursor: 'pointer', borderLeft: '4px solid #dc3545' }}
              >
                <div className="review-stat-number">{needsRevision}</div>
                <div className="review-stat-label">Needs Revision</div>
              </div>
            )}
          </div>

          {/* Sub-filters: status breakdown + content type */}
          {(subStatuses.length > 0 || Object.keys(contentTypeCounts).length > 1) && (
            <div className="review-subfilters">
              {/* Status sub-filters (e.g., Pre-Screen vs Ready for Decision within Action Needed) */}
              {subStatuses.length > 0 && (
                <>
                  {subStatuses.map(s => (
                    subStatusCounts[s.key] > 0 && (
                      <button
                        key={s.key}
                        className={`review-subfilter-chip status ${statusSubFilter === s.key ? 'active' : ''}`}
                        onClick={() => { setStatusSubFilter(statusSubFilter === s.key ? '' : s.key); setContentTypeFilter(''); }}
                      >
                        {s.label} ({subStatusCounts[s.key]})
                      </button>
                    )
                  ))}
                  {Object.keys(contentTypeCounts).length > 1 && (
                    <span className="review-subfilter-divider" />
                  )}
                </>
              )}
              {/* Content type filters */}
              {Object.keys(contentTypeCounts).length > 1 && (
                <>
                  {Object.entries(contentTypeCounts)
                    .sort(([,a], [,b]) => b - a)
                    .map(([ct, count]) => (
                      <button
                        key={ct}
                        className={`review-subfilter-chip type ${contentTypeFilter === ct ? 'active' : ''}`}
                        onClick={() => setContentTypeFilter(contentTypeFilter === ct ? '' : ct)}
                      >
                        {CONTENT_TYPE_LABELS[ct] || ct} ({count})
                      </button>
                    ))
                  }
                </>
              )}
            </div>
          )}

          {/* Queue search (Fredrik 2026-08-12) */}
          {reviews.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <input
                type="search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by title, producer, channel, or type…"
                aria-label="Search review queue"
                style={{
                  width: '100%', maxWidth: 420, padding: '8px 12px',
                  border: '1px solid #ced4da', borderRadius: 8, fontSize: 13
                }}
              />
              {q && (
                <span style={{ marginLeft: 10, fontSize: 12, color: '#6c757d' }}>
                  {visibleReviews.length} match{visibleReviews.length === 1 ? '' : 'es'}
                </span>
              )}
            </div>
          )}

          {/* Bulk actions bar */}
          {canMakeDecisions && reviews.length > 0 && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: '#666' }}>
                <input
                  type="checkbox"
                  checked={selectedForDelete.size === reviews.length && reviews.length > 0}
                  onChange={selectAll}
                />
                Select all
              </label>
              {selectedForDelete.size > 0 && (
                <button
                  className="review-btn review-btn-danger"
                  onClick={handleBulkDelete}
                  disabled={deleting}
                  style={{ fontSize: 12, padding: '5px 12px' }}
                >
                  {deleting ? 'Deleting...' : `Delete ${selectedForDelete.size} selected`}
                </button>
              )}
            </div>
          )}

          {/* Review queue */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Loading...</div>
          ) : error ? (
            <div style={{ color: '#dc3545', padding: 20, background: '#f8d7da', borderRadius: 8 }}>{error}</div>
          ) : visibleReviews.length === 0 ? (
            <div className="review-empty">
              <p>{(statusFilter || contentTypeFilter || q) ? 'No items match this filter.' : 'No content in the review queue.'}</p>
              {!statusFilter && !contentTypeFilter && !q && canManageSubmissions && (
                <p style={{ fontSize: 13, color: '#6c757d' }}>
                  Content enters automatically from SharePoint, or use "Submit Web Pages" to add manually.
                </p>
              )}
              {(statusFilter || contentTypeFilter || statusSubFilter || q) && (
                <button className="review-btn review-btn-outline" onClick={() => { setStatusFilter(''); setStatusSubFilter(''); setContentTypeFilter(''); setSearchQuery(''); }} style={{ fontSize: 13 }}>
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            visibleReviews.map(review => (
              <div
                key={review._id}
                className={`review-card ${(() => {
                  if (!canMakeDecisions && user?.email) {
                    const my = review.reviewerAssessments?.find(a => a.reviewer.email === user.email);
                    return !my?.submittedAt ? 'action-needed' : '';
                  }
                  return (review.status === 'pending_prescreen' || (review.status === 'in_review' && review.allReviewersComplete) || isHeldOnFixes(review)) ? 'action-needed' : '';
                })()}`}
                onClick={() => setSelectedReviewId(review._id)}
              >
                <div className="review-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {canMakeDecisions && (
                      <input
                        type="checkbox"
                        checked={selectedForDelete.has(review._id)}
                        onChange={() => toggleSelectForDelete(review._id)}
                        onClick={e => e.stopPropagation()}
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                      />
                    )}
                    <h4 className="review-card-title">{review.contentItem.name}</h4>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {(review.childCount || 0) > 0 && (
                      <span style={{
                        background: '#e0e7ff', color: '#3730a3', fontSize: 11, fontWeight: 600,
                        padding: '3px 8px', borderRadius: 10
                      }}>
                        +{review.childCount} linked
                      </span>
                    )}
                    {(() => {
                      // For reviewers: show their personal status, and for
                      // in-flight cycles WHICH STEP it is sitting at. Without
                      // the step, "Needs Your Review" and "someone before you
                      // is still working" looked identical (2026-08-20).
                      if (!canMakeDecisions && user?.email) {
                        const stage = reviewerStage(review, user.email, currentUserStep);
                        if (stage === 'approved' || stage === 'revision_needed' || stage === 'rejected') {
                          return <span className={`review-status-badge ${review.status}`}>{STATUS_LABELS[review.status]}</span>;
                        }
                        const step = currentStep(review);
                        const stepPill = step && (
                          <span
                            style={{
                              background: '#f4f6fa', color: '#495057', fontSize: 11,
                              fontWeight: 600, padding: '3px 8px', borderRadius: 10
                            }}
                            title="Step this cycle is currently at"
                          >
                            {STEP_LABEL[step]}
                          </span>
                        );
                        if (stage === 'submitted') {
                          return (
                            <>
                              {stepPill}
                              <span className="review-status-badge submitted">Submitted</span>
                            </>
                          );
                        }
                        if (stage === 'waiting') {
                          return (
                            <>
                              {stepPill}
                              <span
                                className="review-status-badge pending_review"
                                style={{ opacity: 0.65 }}
                                title={`Waiting on ${waitingOnLabel(review)} — not your turn yet`}
                              >
                                Waiting on {waitingOnLabel(review)}
                              </span>
                            </>
                          );
                        }
                        return (
                          <>
                            {stepPill}
                            <span className="review-status-badge pending_review">Your Turn</span>
                          </>
                        );
                      }
                      // For decision makers
                      const label = review.status === 'pending_prescreen' ? 'Pre-Screen'
                        : review.allReviewersComplete && review.status === 'in_review' ? 'Ready for Decision'
                        : isHeldOnFixes(review) ? 'Approved — awaiting fixes'
                        : STATUS_LABELS[review.status];
                      return <span className={`review-status-badge ${review.status}`}>{label}</span>;
                    })()}
                  </div>
                </div>

                {/* Walk station 4 card cleanup (Fredrik 2026-08-13): every
                    chip carries a muted label so "Toolkit / HCP / 0.1" reads
                    as Type / Audience / Version at a glance; the Format chip
                    is skipped when it would duplicate the Type text (a Video-
                    type video rendered two identical "Video" chips); Edit is
                    promoted to an outlined button pinned right. */}
                <div className="review-card-meta" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                  {review.contentItem.type && (
                    <span className="review-meta-tag purpose">
                      <span style={{ fontWeight: 400, marginRight: 4 }}>Type</span>
                      {review.contentItem.type}
                    </span>
                  )}
                  {review.contentItem.audience && (
                    <span className="review-meta-tag audience">
                      <span style={{ fontWeight: 400, marginRight: 4 }}>Audience</span>
                      {review.contentItem.audience}
                    </span>
                  )}
                  {review.channel && (
                    <span className="review-meta-tag" style={{ background: '#f0e9fe', color: '#5b21b6' }}>
                      <span style={{ fontWeight: 400, marginRight: 4 }}>Channel</span>
                      {review.channel}
                    </span>
                  )}
                  {/* v2 (D90) — topic + geography chips (slug-stored;
                      labels resolved via dimLabels). Geography hidden for
                      global (the default carries no information). */}
                  {(review.contentItem as any).topic && (
                    <span className="review-meta-tag" style={{ background: '#e7f5ec', color: '#1e6b3a' }}>
                      <span style={{ fontWeight: 400, marginRight: 4 }}>Topic</span>
                      {dimLabels[(review.contentItem as any).topic] || (review.contentItem as any).topic}
                    </span>
                  )}
                  {(review.contentItem as any).geography && (review.contentItem as any).geography !== 'global' && (
                    <span className="review-meta-tag" style={{ background: '#fdf1e0', color: '#8a5a00' }}>
                      <span style={{ fontWeight: 400, marginRight: 4 }}>Geo</span>
                      {dimLabels[(review.contentItem as any).geography] || (review.contentItem as any).geography}
                    </span>
                  )}
                  {/* O-7 (joint critical review, 2026-08-14) — post-approval
                      automation status was rendered ONLY in DecisionView, so a
                      producer's card read "Approved" even when the Content Hub
                      upload had failed: invisible to the producer, unactionable
                      by the DM, and found by whoever went looking for the file.
                      Failures surface here; successes stay quiet so the cards
                      don't fill with noise. */}
                  {(() => {
                    const auto = automationSummary(review.automation as any);
                    if (!auto.ran || !auto.hasFailure) return null;
                    const names = auto.failed
                      .map(f => AUTOMATION_STEP_LABELS[f.step] || f.step)
                      .join(', ');
                    return (
                      <span
                        className="review-meta-tag"
                        data-testid="automation-failed-chip"
                        style={{ background: '#f8d7da', color: '#a6231a', fontWeight: 600 }}
                        title={`Publishing did not complete: ${names}. Open the cycle to see details and re-run.`}
                      >
                        ⚠ Publishing incomplete
                      </span>
                    );
                  })()}
                  {review.contentType &&
                    (CONTENT_TYPE_LABELS[review.contentType] || review.contentType).toLowerCase() !== (review.contentItem.type || '').toLowerCase() && (
                    <span className="review-meta-tag format">
                      <span style={{ fontWeight: 400, marginRight: 4 }}>Format</span>
                      {CONTENT_TYPE_LABELS[review.contentType] || review.contentType}
                    </span>
                  )}
                  {review.sourceEnv === 'production' && (
                    <span
                      className="review-meta-tag"
                      style={{ backgroundColor: '#fce8e6', color: '#a50e0e', fontWeight: 600 }}
                      title="This web page is on a production host (cellcolabs.com / cellcolabsclinical.com)"
                    >
                      Production
                    </span>
                  )}
                  {review.contentItem.version && (
                    <span className="review-meta-tag version" title="Draft version (X.Y — approvals stamp V<major>.0 in the archive)">
                      V{review.contentItem.version}
                    </span>
                  )}
                  {/* 14e-C — editable source status chip. Reads
                      contentItem.source* set at submit; shows an at-a-glance
                      indicator so producers / DMs can see whether the
                      working-source policy (D81) was satisfied on this cycle
                      without opening the modal. Silent when no source signal
                      exists AND the cycle isn't a pdf → Content Hub (which
                      is the only path where source is required). */}
                  {(() => {
                    const ci: any = review.contentItem || {};
                    if (ci.sourceFileId) {
                      return (
                        <span
                          className="review-meta-tag"
                          style={{ backgroundColor: '#e6f4ea', color: '#1e6f2c', fontWeight: 500 }}
                          title={`Editable companion attached: ${ci.sourceFileName || 'source file'}`}
                        >
                          📎 Source file
                        </span>
                      );
                    }
                    if (ci.sourceLink) {
                      // Walk station 4 (Fredrik): the card row is one big
                      // click target (opens the cycle), so this must be a
                      // real anchor that stops propagation — same pattern
                      // as the Edit button. vm: pointers aren't URLs;
                      // render them unlinked with the pointer in the title.
                      const isVm = String(ci.sourceLink).startsWith('vm:');
                      return isVm ? (
                        <span
                          className="review-meta-tag"
                          style={{ backgroundColor: '#e8f0fe', color: '#1a58c2', fontWeight: 500 }}
                          title={`Source (VM project): ${ci.sourceLink}`}
                        >
                          🔗 Source (VM)
                        </span>
                      ) : (
                        <a
                          className="review-meta-tag"
                          href={ci.sourceLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ backgroundColor: '#e8f0fe', color: '#1a58c2', fontWeight: 500, textDecoration: 'none', cursor: 'pointer' }}
                          title={`Open editable source: ${ci.sourceLink}`}
                        >
                          🔗 Source link
                        </a>
                      );
                    }
                    if (ci.noEditableSourceReason) {
                      return (
                        <span
                          className="review-meta-tag"
                          style={{ backgroundColor: '#fff4e5', color: '#a15c00', fontWeight: 500 }}
                          title={`Source waived — reason: ${ci.noEditableSourceReason}`}
                        >
                          ⚠ No source ({ci.noEditableSourceReason})
                        </span>
                      );
                    }
                    return null;
                  })()}
                  {/* Missing metadata warning + edit button for admins */}
                  {canEditMetadata(review) && (!review.contentItem.type || !review.contentItem.audience) && (
                    <button
                      className="review-meta-tag-missing"
                      onClick={(e) => openMetadataEditor(review, e)}
                      title="Fill in missing metadata"
                    >
                      + Add details
                    </button>
                  )}
                  {review.reviewDeadline && review.status !== 'approved' && review.status !== 'rejected' && (
                    <DeadlinePill deadline={review.reviewDeadline} />
                  )}
                  {/* Fix pass 2026-08-10 (Finding 2) — surface producer.name
                      in the dashboard row so DMs/reviewers can see who
                      submitted at a glance. Only for cycles with the
                      Phase 8D+ producer field populated. */}
                  {review.producer?.name && (
                    <span
                      className="review-meta-tag"
                      style={{ background: '#eef2ff', color: '#3730a3' }}
                      title={`Submitted by ${review.producer.name}`}
                    >
                      by {review.producer.name}
                    </span>
                  )}
                  {/* Right-aligned group: date + a properly visible Edit. */}
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#999', fontSize: 12 }}>{new Date(review.createdAt).toLocaleDateString()}</span>
                    {canEditMetadata(review) && review.contentItem.type && review.contentItem.audience && (
                      <button
                        onClick={(e) => openMetadataEditor(review, e)}
                        title="Edit content details"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          padding: '5px 12px',
                          background: '#FFFFFF',
                          color: '#123250',
                          border: '1px solid #123250',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#123250'; (e.currentTarget as HTMLElement).style.color = '#FFFFFF'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#FFFFFF'; (e.currentTarget as HTMLElement).style.color = '#123250'; }}
                      >
                        ✎ Edit
                      </button>
                    )}
                  </span>
                </div>

                {/* Reviewer assessments (only show when in review) */}
                {review.reviewerAssessments.length > 0 && review.status !== 'pending_prescreen' && (
                  <div className="risk-matrix">
                    {review.reviewerAssessments.map((a, i) => (
                      <div key={i} className="risk-matrix-item">
                        <span>{a.reviewer?.name || 'Reviewer'}: </span>
                        {/* submittedAt, not the deprecated `decision` field —
                            the third member of that family found today (the
                            backend's allReviewersComplete recompute and this
                            row both kept reading a field reviewers stopped
                            writing, so every submitted assessment showed
                            Pending forever). */}
                        {a.submittedAt ? (
                          <span className={`risk-badge ${a.riskLevel || 'low'}`}>{a.riskLevel || 'submitted'}</span>
                        ) : (
                          <span style={{ color: '#999', fontSize: 12 }}>Pending</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Link as child action */}
                {canManageSubmissions && linkingParent === null && (
                  <button
                    className="review-btn review-btn-outline"
                    onClick={(e) => { e.stopPropagation(); setLinkingParent(review._id); }}
                    style={{ fontSize: 11, padding: '4px 10px', marginTop: 8 }}
                  >
                    Link as child of...
                  </button>
                )}

                {/* When in linking mode, show "Set as parent" on other cards */}
                {linkingParent && linkingParent !== review._id && (
                  <button
                    className="review-btn review-btn-primary"
                    onClick={(e) => { e.stopPropagation(); handleLinkAsChild(linkingParent, review._id); }}
                    style={{ fontSize: 11, padding: '4px 10px', marginTop: 8 }}
                  >
                    Set as parent of selected item
                  </button>
                )}

                {linkingParent === review._id && (
                  <div style={{ fontSize: 12, color: '#123250', fontWeight: 600, marginTop: 8 }}>
                    Click another item to set it as parent
                    <button
                      className="review-btn review-btn-outline"
                      onClick={(e) => { e.stopPropagation(); setLinkingParent(null); }}
                      style={{ fontSize: 11, padding: '2px 8px', marginLeft: 8 }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </>
      )}

      {/* Metadata editor modal */}
      {editingMetadata && (
        <div className="review-modal-overlay" onClick={() => setEditingMetadata(null)}>
          <div className="review-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3>Edit Content Details</h3>
            <div className="review-form-group">
              <label>Title</label>
              <input
                value={metaForm.name}
                onChange={e => setMetaForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="review-form-group">
              <label>
                Producer{' '}
                <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                  (who this content belongs to — drives their dashboard and notifications)
                </span>
              </label>
              <select
                value={metaProducerId}
                disabled={!REASSIGNABLE_STATUSES.includes(metaStatus)}
                onChange={e => setMetaProducerId(e.target.value)}
              >
                {metaTeam.length === 0 && <option value="">Team list unavailable</option>}
                {metaTeam.map(m => (
                  <option key={m._id} value={m._id}>{m.name}</option>
                ))}
              </select>
              {!REASSIGNABLE_STATUSES.includes(metaStatus) && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#856404' }}>
                  Producer can't be reassigned once a cycle reaches "{metaStatus}" — the
                  review record is part of the audit trail.
                </p>
              )}
            </div>
            <div className="review-form-group">
              <label>
                Public name{' '}
                <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                  (optional — filename shown to external users on HubSpot)
                </span>
              </label>
              <input
                value={metaForm.publicName}
                onChange={e => setMetaForm(f => ({ ...f, publicName: e.target.value }))}
                placeholder={metaForm.name || 'Defaults to Title'}
              />
            </div>
            {/* WS2 walk finding #1 (2026-08-11) — edit mirrors creation:
                registry-fed Type + Channel with inline add. Changing
                Channel re-routes postApprovalAction server-side. */}
            <div className="review-form-group">
              <label>Type</label>
              <TaxonomySelect
                kind="type"
                value={metaForm.type}
                onChange={type => setMetaForm(f => ({ ...f, type }))}
                getAuthHeaders={getAuthHeaders}
              />
            </div>
            {/* Fredrik 2026-08-12 (UI walk, station 3) — surface the
                detected format read-only. It's a property of the file
                (not editable), but it silently filters the channel list
                below, so the user must be able to see it. */}
            <div className="review-form-group">
              <label>Format</label>
              <div style={{ fontSize: 13, color: '#495057', padding: '6px 0' }}>
                {metaFormat ? (
                  <>
                    <strong>{metaFormat.toUpperCase()}</strong>
                    <span style={{ fontSize: 11, color: '#6c757d', marginLeft: 8 }}>
                      detected from the file — determines which channels are available below
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: 12, color: '#6c757d' }}>
                    unknown (legacy cycle) — channel list not filtered
                  </span>
                )}
              </div>
            </div>
            <div className="review-form-group">
              <label>
                Channel{' '}
                <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                  (where this goes after approval)
                </span>
              </label>
              <TaxonomySelect
                kind="channel"
                value={metaForm.channel}
                onChange={channel => setMetaForm(f => ({ ...f, channel }))}
                getAuthHeaders={getAuthHeaders}
                filterFormat={metaFormat}
              />
            </div>
            <div className="review-form-group">
              <label>Target Audience</label>
              <TaxonomySelect
                kind="audience"
                value={metaForm.audience}
                onChange={audience => setMetaForm(f => ({ ...f, audience }))}
                getAuthHeaders={getAuthHeaders}
              />
            </div>
            {/* v2 (D90) — lineage dims editable; topic can change, never clear. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="review-form-group">
                <label>Topic</label>
                <TaxonomySelect
                  kind="topic"
                  value={metaForm.topic}
                  onChange={topic => setMetaForm(f => ({ ...f, topic }))}
                  getAuthHeaders={getAuthHeaders}
                  placeholder="Select topic..."
                />
              </div>
              <div className="review-form-group">
                <label>Geography</label>
                <TaxonomySelect
                  kind="geography"
                  value={metaForm.geography}
                  onChange={geography => setMetaForm(f => ({ ...f, geography }))}
                  getAuthHeaders={getAuthHeaders}
                  placeholder="Global"
                />
              </div>
            </div>
            {/* 14e-A walk station 4 — editable source late-attach/replace.
                Mirrors the submit modal; required marker follows the same
                format rule (pdf/image/video). The companion FILE isn't
                replaceable here (that's a resubmit concern) — link + audited
                exception are. Server re-runs the D81 gate on the merged
                result, so clearing a required source without a reason 400s. */}
            {metaFormat !== 'webpage' && (() => {
              const isSourceRequired = metaFormat !== null && ['pdf', 'image', 'video'].includes(metaFormat);
              const hasReason = Boolean(metaForm.noEditableSourceReason);
              return (
                <div className="review-form-group" style={{ paddingTop: 10, borderTop: '1px solid #eee' }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>
                    Editable source
                    {isSourceRequired && (
                      <span style={{ color: '#c73535', marginLeft: 4 }} title="Required — the editable original lives outside this file">*</span>
                    )}{' '}
                    <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                      (link to where the editable original lives)
                    </span>
                  </label>
                  {metaForm.sourceFileId && (
                    <div style={{
                      fontSize: 12, marginBottom: 8, padding: '8px 10px',
                      background: '#f4f8f4', border: '1px solid #cfe0cf', borderRadius: 6
                    }}>
                      Working file attached: <strong>{metaForm.sourceFileName || '(unnamed)'}</strong>
                      <div style={{ color: '#666', margin: '4px 0 6px' }}>
                        This file — not the submitted one — is copied into Approved content - working
                        files on approval.
                      </div>
                      <button
                        type="button"
                        onClick={() => setMetaForm(f => ({ ...f, sourceFileId: null, sourceFileName: null }))}
                        style={{
                          fontSize: 12, cursor: 'pointer', background: '#fff',
                          border: '1px solid #bbb', borderRadius: 4, padding: '3px 10px'
                        }}
                      >
                        Detach working file
                      </button>
                    </div>
                  )}
                  <input
                    type="text"
                    value={metaForm.sourceLink}
                    onChange={e => setMetaForm(f => ({
                      ...f,
                      sourceLink: e.target.value,
                      // Typing a link clears the exception — they're either/or.
                      noEditableSourceReason: e.target.value ? '' : f.noEditableSourceReason
                    }))}
                    placeholder={metaFormat === 'video'
                      ? 'https://frame.io/… or vm:render-vm/projects/….aep'
                      : metaFormat === 'image'
                        ? 'https://figma.com/file/…'
                        : 'https://cellcoab.sharepoint.com/… or https://drive.google.com/…'}
                    disabled={hasReason}
                    style={{ opacity: hasReason ? 0.5 : 1 }}
                  />
                  {isSourceRequired && (
                    <div style={{
                      marginTop: 8,
                      padding: '8px 10px',
                      background: hasReason ? '#fff8ee' : '#fafafa',
                      border: `1px solid ${hasReason ? '#f0d9b5' : '#eee'}`,
                      borderRadius: 8,
                      fontSize: 12
                    }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer', color: '#444', fontWeight: 500 }}>
                        <input
                          type="checkbox"
                          checked={hasReason}
                          onChange={e => setMetaForm(f => ({
                            ...f,
                            noEditableSourceReason: e.target.checked ? (f.noEditableSourceReason || 'legacy') : '',
                            sourceLink: e.target.checked ? '' : f.sourceLink
                          }))}
                          style={{ width: 14, height: 14, margin: 0, flexShrink: 0 }}
                        />
                        No editable source available
                        <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(audited exception)</span>
                      </label>
                      {hasReason && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, paddingLeft: 22 }}>
                          <span style={{ fontSize: 11, color: '#8a6d3b', flexShrink: 0 }}>Reason</span>
                          <select
                            value={metaForm.noEditableSourceReason}
                            onChange={e => setMetaForm(f => ({ ...f, noEditableSourceReason: e.target.value }))}
                            style={{ flex: '0 1 240px', padding: '4px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}
                          >
                            <option value="legacy">Legacy content (predates working-files policy)</option>
                            <option value="scanned">Scanned / OCR only</option>
                            <option value="third-party">Third-party file (no source available)</option>
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="review-action-bar">
              <button className="review-btn review-btn-primary" onClick={handleSaveMetadata}>
                Save
              </button>
              <button className="review-btn review-btn-outline" onClick={() => setEditingMetadata(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit for Review modal (web pages) */}
      {showSubmitModal && (
        <SubmitForReviewModal
          user={user}
          getAuthHeaders={getAuthHeaders}
          onSubmitted={() => { setShowSubmitModal(false); fetchReviews(); setTaxonomyVersion(v => v + 1); }}
          onClose={() => setShowSubmitModal(false)}
        />
      )}

      {/* Phase 8C — Submit Content chooser */}
      {submitFlow === 'chooser' && (
        <SubmitChooserModal
          onPickFile={() => setSubmitFlow('file-queue')}
          onPickWebPage={() => { setSubmitFlow(null); setShowSubmitModal(true); }}
          onClose={() => setSubmitFlow(null)}
        />
      )}

      {/* Phase 8C — SharePoint file picker */}
      {submitFlow === 'file-queue' && (
        <SubmissionQueueModal
          getAuthHeaders={getAuthHeaders}
          onPick={(file) => setSubmitFlow({ kind: 'file-form', file })}
          onClose={() => setSubmitFlow(null)}
        />
      )}

      {/* Phase 8C — file metadata form */}
      {submitFlow && typeof submitFlow === 'object' && submitFlow.kind === 'file-form' && (
        <SubmitFileMetadataModal
          user={user}
          file={submitFlow.file}
          getAuthHeaders={getAuthHeaders}
          onBack={() => setSubmitFlow('file-queue')}
          onSubmitted={() => { setSubmitFlow(null); fetchReviews(); setTaxonomyVersion(v => v + 1); }}
          onClose={() => setSubmitFlow(null)}
        />
      )}
    </div>
  );
};

// ============================================
// Submit for Review Modal
// ============================================
interface SubmitModalProps {
  user: User | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onSubmitted: () => void;
  onClose: () => void;
}

interface TeamMemberLite {
  _id: string;
  name: string;
  email: string;
}

// Mirrors REASSIGNABLE_STATUSES in backend/routes/contentReview.js. Kept in
// step deliberately: the server is still the authority and returns 409, but the
// UI should explain the rule rather than let the user discover it by failing.
const REASSIGNABLE_STATUSES = ['pending_prescreen', 'pending_review', 'in_review', 'revision_needed'];

// 2026-08-24 (Fredrik) — Site/Purpose is now a ROUTING key, not just a label:
// it decides which public domain an approved page publishes to
// (backend/services/contentReview/webPublicUrl.js). Every option here must
// have a mapping there, so 'Public Web (Other)' was removed — it named a
// property that may never be used, and an option with no domain can only
// produce an archive entry that cannot say where the page goes. If it comes
// back, it gets its mapping in the same change.
//
// 'Other' deliberately stays and deliberately has no mapping: it is the escape
// hatch for a page that is genuinely none of the above, and the archive says
// "unresolved" for it rather than inventing a link.
const WEB_PURPOSE_OPTIONS = [
  { value: 'Portal (HCP)', label: 'Portal (HCP)' },
  { value: 'Cellcolabs.com Public', label: 'Cellcolabs.com (Public)' },
  { value: 'Cellcolabs Clinical Public', label: 'Cellcolabs Clinical (Public)' },
  { value: 'Other Web Page', label: 'Other' },
];

// Exported so ProducerDashboard can reuse the same submit-flow modals.
export const SubmitForReviewModal: React.FC<SubmitModalProps> = ({ user, getAuthHeaders, onSubmitted, onClose }) => {
  const [urls, setUrls] = useState('');
  const [purpose, setPurpose] = useState('Portal (HCP)');
  // WS2 (D75) — URL submissions default to the Web channel; overridable
  // (e.g. a Portal page reviewed by URL should route as Portal).
  const [channel, setChannel] = useState('Web');
  // Walk finding #5 (2026-08-11) — web submissions never collected an
  // Audience, so approved web cycles failed Archive validation. Required
  // now, aligned with the file flow.
  const [audience, setAudience] = useState('');
  // 2026-08-18 — D90/D94 made topic a REQUIRED lineage dimension for both
  // submit-file and from-url (resolveLineageDims, contentReview.js), but only
  // the file modal got the field: this one posted no topic at all, so every
  // web-page submission 400'd with TOPIC_REQUIRED. Found during the prod E2E
  // prep. Geography defaults to 'global', matching the file modal.
  const [topic, setTopic] = useState('');
  const [geography, setGeography] = useState('global');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState('');

  // Phase 8D — producer picker. Anyone can attribute to anyone (covers
  // "submitting on behalf of Sara" cases). Defaults to the current user
  // by email-match against the team list. No permission gate at upload.
  const [team, setTeam] = useState<TeamMemberLite[]>([]);
  const [producerId, setProducerId] = useState<string>('');
  // 2026-08-19 (Fredrik): web submissions were always titled with the raw URL,
  // because this modal never offered a title even though from-url has accepted
  // `name` all along (150-char validated, falling back to `name || url`). A
  // URL-titled cycle is hard to find in archive search later, and renaming
  // after the fact only happens if someone remembers — and an approved cycle
  // cannot be deleted if they don't.
  const [title, setTitle] = useState('');
  // Surfaced rather than swallowed: if the team fetch fails the producer
  // <select> used to sit on "Loading team..." forever, which looks identical to
  // a slow network and leaves no way to attribute the submission.
  const [teamError, setTeamError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/team`, { headers });
        if (cancelled) return;
        const list: TeamMemberLite[] = (res.data || []).map((m: any) => ({
          _id: m._id, name: m.name, email: m.email
        }));
        setTeam(list);
        const self = list.find(m => m.email?.toLowerCase() === user?.email?.toLowerCase());
        if (self) setProducerId(self._id);
      } catch (err: any) {
        console.error('Error fetching team for producer picker:', err);
        if (!cancelled) setTeamError(err?.response?.data?.error || 'Could not load the team list');
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeaders, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const urlList = urls.split('\n').map(u => u.trim()).filter(u => u.length > 0);
    if (urlList.length === 0) {
      setError('Please enter at least one URL');
      return;
    }
    if (!audience) {
      setError('Please select a target audience');
      return;
    }
    if (!topic) {
      setError('Please select a topic — pick the portal category this content belongs to, or "Other"');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setProgress(`Submitting ${urlList.length} page${urlList.length > 1 ? 's' : ''}...`);
      const headers = await getAuthHeaders();

      await axios.post(`${backendUrl}/api/content-review/cycles/from-url`, {
        urls: urlList.map(url => ({ url, name: title.trim() || url })),
        purpose,
        channel,
        audience,
        topic,
        geography,
        comment: comment.trim(),
        producer: producerId ? { teamMemberId: producerId } : undefined
      }, { headers });

      onSubmitted();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit for review');
    } finally {
      setSubmitting(false);
      setProgress('');
    }
  };

  return (
    <div className="review-modal-overlay" onClick={onClose}>
      <div className="review-modal" onClick={e => e.stopPropagation()}>
        <h3>Submit Web Pages for Review</h3>

        <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
          Enter the URLs of web pages to submit for content review. A screenshot and page content will be captured automatically.
        </p>
        <p style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
          Allowed hosts: staging (<code>www-cellcolabsclinical-com.sandbox.hs-sites-eu1.com</code>,{' '}
          <code>www-cellcolabs-com.sandbox.hs-sites-eu1.com</code>), sandbox (<code>148083878.hs-sites-eu1.com</code>,{' '}
          <code>147214542.hs-sites-eu1.com</code>) or production (<code>cellcolabs.com</code> / <code>cellcolabsclinical.com</code>).
          Review normally happens before publishing, so a staging URL is the usual case; production submissions get a visible badge in the queue.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="review-form-group">
            <label>Page URLs (one per line)</label>
            <textarea
              value={urls}
              onChange={e => setUrls(e.target.value)}
              placeholder={"https://www-cellcolabsclinical-com.sandbox.hs-sites-eu1.com/en/about-us/\nhttps://148083878.hs-sites-eu1.com/page-2"}
              rows={4}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="review-form-group">
              <label>Site / Purpose</label>
              <select value={purpose} onChange={e => setPurpose(e.target.value)}>
                {WEB_PURPOSE_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                Sets the public domain the approved page is recorded against —
                pages reviewed on staging publish to this site.
              </div>
            </div>
            {/* WS2 (D75) — routing channel; Web is the sensible default for
                URL reviews, Portal pages should pick Portal. */}
            <div className="review-form-group">
              <label>
                Channel{' '}
                <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                  (where this goes after approval)
                </span>
              </label>
              <TaxonomySelect
                kind="channel"
                value={channel}
                onChange={setChannel}
                getAuthHeaders={getAuthHeaders}
                filterFormat="webpage"
              />
            </div>
            {/* Walk finding #5 — Audience required (drives archive metadata
                + HubSpot access levels), aligned with the file flow. */}
            <div className="review-form-group">
              <label>Target Audience</label>
              {/* Walk finding #6 — audiences registry-fed + extensible. */}
              <TaxonomySelect
                kind="audience"
                value={audience}
                onChange={setAudience}
                getAuthHeaders={getAuthHeaders}
              />
            </div>
            {/* D90/D94 — topic is a required lineage dimension (it becomes a
                folder level in the archive + working-file trees), so it has to
                be collected here exactly as the file flow collects it. */}
            <div className="review-form-group">
              <label>
                Topic{' '}
                <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                  (portal category — pick "Other" if none fits)
                </span>
              </label>
              <TaxonomySelect
                kind="topic"
                value={topic}
                onChange={setTopic}
                getAuthHeaders={getAuthHeaders}
                placeholder="Select topic..."
              />
            </div>
            <div className="review-form-group">
              <label>Geography</label>
              <TaxonomySelect
                kind="geography"
                value={geography}
                onChange={setGeography}
                getAuthHeaders={getAuthHeaders}
                placeholder="Global"
              />
            </div>
          </div>

          <div className="review-form-group">
            <label>
              Title{' '}
              <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                (optional — defaults to the URL; max 150 characters)
              </span>
            </label>
            <input
              type="text"
              value={title}
              maxLength={150}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. About Us page - Q3 refresh"
            />
          </div>

          <div className="review-form-group">
            <label>
              Producer{' '}
              <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                (defaults to you; pick another team member if submitting on their behalf)
              </span>
            </label>
            <select value={producerId} onChange={e => setProducerId(e.target.value)}>
              {team.length === 0 && <option value="">{teamError ? 'Team list unavailable' : 'Loading team...'}</option>}
              {team.map(m => (
                <option key={m._id} value={m._id}>
                  {m.name}{user?.email && m.email?.toLowerCase() === user.email.toLowerCase() ? ' (you)' : ''}
                </option>
              ))}
            </select>
            {teamError && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#a4262c' }}>
                {teamError} — the submission will be attributed to you. Reload to try again.
              </p>
            )}
          </div>

          <div className="review-form-group">
            <label>Notes (optional)</label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Context for reviewers, e.g., what changed, what to focus on..."
              rows={2}
            />
          </div>

          {error && <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 12 }}>{error}</div>}
          {progress && <div style={{ color: '#123250', fontSize: 13, marginBottom: 12 }}>{progress}</div>}

          <div className="review-action-bar">
            <button type="submit" className="review-btn review-btn-primary" disabled={submitting}>
              {submitting ? progress || 'Submitting...' : `Submit ${urls.split('\n').filter(u => u.trim()).length || 0} page${urls.split('\n').filter(u => u.trim()).length !== 1 ? 's' : ''} for Review`}
            </button>
            <button type="button" className="review-btn review-btn-outline" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// Phase 8C — Submit Content chooser + queue + metadata form
// ============================================

// WS2 (D75) + walk finding #6 — the fixed PURPOSE_OPTIONS and
// AUDIENCE_OPTIONS lists are retired; Type, Channel, and Audience all
// come from the taxonomy registry via <TaxonomySelect/>.

interface SubmitChooserProps {
  onPickFile: () => void;
  onPickWebPage: () => void;
  onClose: () => void;
}

export const SubmitChooserModal: React.FC<SubmitChooserProps> = ({ onPickFile, onPickWebPage, onClose }) => (
  <div className="review-modal-overlay" onClick={onClose}>
    <div className="review-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
      <h3 style={{ color: '#1a1a1a' }}>Submit Content for Review</h3>
      <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
        What are you submitting? <GuideLink href={ROLE_GUIDES.producerFiles} label="File rules" />
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <button
          type="button"
          onClick={onPickFile}
          style={{
            padding: '20px 16px', textAlign: 'left', border: '1px solid #ddd',
            borderRadius: 8, background: '#fff', cursor: 'pointer', color: '#1a1a1a'
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: '#1a1a1a' }}>File</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            Pick a file you've placed in SharePoint <strong>Ready to Review</strong> and fill in the metadata.
          </div>
        </button>
        <button
          type="button"
          onClick={onPickWebPage}
          style={{
            padding: '20px 16px', textAlign: 'left', border: '1px solid #ddd',
            borderRadius: 8, background: '#fff', cursor: 'pointer', color: '#1a1a1a'
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: '#1a1a1a' }}>Web Page</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            Submit a sandbox or production URL. We capture an interactive snapshot for reviewers.
          </div>
        </button>
      </div>
      <div className="review-action-bar">
        <button type="button" className="review-btn review-btn-outline" onClick={onClose}>Cancel</button>
      </div>
    </div>
  </div>
);

interface SubmissionQueueProps {
  getAuthHeaders: () => Promise<Record<string, string>>;
  onPick: (file: SubmissionQueueFile) => void;
  onClose: () => void;
}

export const SubmissionQueueModal: React.FC<SubmissionQueueProps> = ({ getAuthHeaders, onPick, onClose }) => {
  const [files, setFiles] = useState<SubmissionQueueFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/content-review/submission-queue`, { headers });
        if (cancelled) return;
        setFiles(res.data?.files || []);
      } catch (err: any) {
        if (cancelled) return;
        setError(err.response?.data?.error || 'Failed to load submission queue');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  return (
    <div className="review-modal-overlay" onClick={onClose}>
      <div className="review-modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: '#1a1a1a' }}>Pick a file to submit</h3>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
          Files in SharePoint <strong>Ready to Review</strong> that aren't tied to a review yet. Drop new files
          into that folder via SharePoint, then come back here to submit them.
        </p>

        {loading && <p style={{ fontSize: 13, color: '#999' }}>Loading SharePoint folder...</p>}
        {error && <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {files && files.length === 0 && !error && (
          <p style={{ fontSize: 13, color: '#999' }}>No unsubmitted files in Ready to Review.</p>
        )}

        {files && files.length > 0 && (
          <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
            {files.map(f => (
              <button
                key={f.fileId}
                type="button"
                onClick={() => onPick(f)}
                style={{
                  width: '100%', padding: '12px 14px', textAlign: 'left',
                  background: 'transparent', border: 'none', borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer', color: '#1a1a1a'
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a' }}>{f.fileName}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  {f.size ? `${Math.round(f.size / 1024)} KB` : ''}
                  {f.lastModified ? ` · modified ${new Date(f.lastModified).toLocaleDateString()}` : ''}
                  {f.parsedMeta ? ' · convention-named' : ' · needs metadata'}
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="review-action-bar" style={{ marginTop: 16 }}>
          <button type="button" className="review-btn review-btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

interface SubmitFileMetadataProps {
  user: User | null;
  file: SubmissionQueueFile;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onBack: () => void;
  onSubmitted: () => void;
  onClose: () => void;
  // "Submit update" of an approved lineage (2026-09-01): identity fields
  // come prefilled from the lineage head so the next version cannot fork
  // onto a new thread by a re-typed title. Prefill beats the filename parse.
  prefill?: { title?: string; purpose?: string; audience?: string; topic?: string; geography?: string; channel?: string; publicName?: string };
}

export const SubmitFileMetadataModal: React.FC<SubmitFileMetadataProps> = ({ user, file, getAuthHeaders, onBack, onSubmitted, onClose, prefill }) => {
  const pre = file.parsedMeta || null;
  const [purpose, setPurpose] = useState(prefill?.purpose || pre?.purpose || '');
  // WS2 (D75) — Channel is the routing dimension, picked explicitly.
  const [channel, setChannel] = useState(prefill?.channel || '');
  // Fredrik 2026-08-11 — detected Format filters the Channel options
  // (e.g. a .pptx can't pick Content Hub — PDFs manage better there).
  // Walk fix F9 (2026-08-12): MUST mirror the backend's
  // fileValidationService.detectContentType exactly — this map previously
  // treated .doc/.ppt as docx/pptx while the server maps them to null
  // (they aren't in ACCEPTED_EXTENSIONS at all), so the dropdown filtered
  // by one rule set and the server backstop enforced another.
  const detectedFormat = (() => {
    const ext = (file.fileName.match(/\.([^./]+)$/)?.[1] || '').toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (ext === 'docx') return 'docx';
    if (ext === 'pptx') return 'pptx';
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
    if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return 'video';
    return null;
  })();
  const [audience, setAudience] = useState(prefill?.audience || pre?.audience || '');
  const [title, setTitle] = useState(prefill?.title || pre?.title || '');
  // Q4 (2026-08-11) — optional externally-visible name for the HubSpot
  // production copy. Empty = derive from Title.
  const [publicName, setPublicName] = useState(prefill?.publicName || '');
  // Strip a leading "V" if the parsed filename had one — backend canonical
  // form is X.Y, the "V" goes back on at rename time. Walk station 3
  // (2026-08-13): the filename parse can hand over strings that aren't
  // valid draft versions (a bare "1" from "…V1.pptx"); only accept a
  // well-formed X.Y prefill, otherwise leave empty for the lineage
  // suggestion below.
  const parsedVersion = (pre?.version || '').replace(/^v/i, '');
  const [version, setVersion] = useState(/^\d+\.\d+$/.test(parsedVersion) ? parsedVersion : '');
  // True once the producer hand-edits the field — the auto-suggestion
  // never overwrites a manual value.
  const [versionTouched, setVersionTouched] = useState(false);
  const [versionHint, setVersionHint] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [team, setTeam] = useState<TeamMemberLite[]>([]);
  const [producerId, setProducerId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  // Surfaced when the backend detects byte-identical file already in the
  // system. Lets the producer "submit anyway" with the override flag.
  const [hashConflict, setHashConflict] = useState<{ _id: string; name?: string | null; version?: string | null; status?: string } | null>(null);
  // 14e-A (D81) — editable source capture. Required for pdf → Content Hub;
  // optional elsewhere. v1 UI supports sourceLink (paste URL to Drive /
  // SharePoint / Figma / vm: pointer) OR the exception checkbox with a
  // reason. Direct SharePoint file-pick lands in 14e-B.
  const [sourceLink, setSourceLink] = useState('');
  const [audienceChannelWarning, setAudienceChannelWarning] = useState<string | null>(null);
  const [noSourceChecked, setNoSourceChecked] = useState(false);
  const [noSourceReason, setNoSourceReason] = useState<'' | 'legacy' | 'scanned' | 'third-party'>('');
  // 2026-08-18 (D81, Fredrik) — the reviewed artifact is the PDF; docx/pptx are
  // WORKING FILES, not distributables. So a pdf/image/video cycle can now carry
  // a real companion file, which workingFilesService copies into
  // `Approved content - working files/...` at approval (resolveSource ->
  // 'companion'). Previously the UI only offered a link, which records a
  // pointer and copies nothing — the editable master never landed anywhere.
  const [sourceFileId, setSourceFileId] = useState('');
  const [sourceFileName, setSourceFileName] = useState('');
  const [sourceCandidates, setSourceCandidates] = useState<SubmissionQueueFile[] | null>(null);
  const [sourceCandidatesError, setSourceCandidatesError] = useState<string | null>(null);
  // v2 (D90/D94) — slug-stored lineage dimensions. Topic REQUIRED
  // (mandatory-with-Other); geography defaults to global so the common
  // case costs no clicks.
  const [topic, setTopic] = useState(prefill?.topic || '');
  const [geography, setGeography] = useState(prefill?.geography || 'global');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/team`, { headers });
        if (cancelled) return;
        const list: TeamMemberLite[] = (res.data || []).map((m: any) => ({
          _id: m._id, name: m.name, email: m.email
        }));
        setTeam(list);
        const self = list.find(m => m.email?.toLowerCase() === user?.email?.toLowerCase());
        if (self) setProducerId(self._id);
      } catch (err) {
        console.error('Error fetching team for producer picker:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeaders, user]);

  // Walk station 3 — live lineage-aware version suggestion. Debounced on
  // Type + Audience + Title; fills the field only while the producer
  // hasn't hand-edited it. knownLineage drives the hint phrasing.
  useEffect(() => {
    if (!purpose || !audience || !title.trim()) { setVersionHint(null); return; }
    // topic/geography scope the lineage (D90) — they re-trigger the lookup.
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/content-review/suggest-version`, {
          headers,
          params: { purpose, audience, title: title.trim(), topic: topic || undefined, geography: geography || undefined }
        });
        if (cancelled) return;
        const { suggestion, knownLineage } = res.data || {};
        if (!suggestion) return;
        setVersionHint(knownLineage
          ? `Suggested ${suggestion} — this Type + Audience + Title matches an existing lineage`
          : `Suggested ${suggestion} — first submission for this Type + Audience + Title`);
        if (!versionTouched) setVersion(suggestion);
      } catch {
        // Suggestion is best-effort — silent on failure.
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purpose, audience, title, topic, geography]);

  // Companion candidates: other unclaimed editable files sitting in Ready to
  // Review. Same endpoint the picker modal uses, so a file already attached to
  // another cycle (as content OR as a source) is filtered out server-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/content-review/submission-queue`, { headers });
        if (cancelled) return;
        const editable = (res.data?.files || []).filter((f: SubmissionQueueFile) =>
          f.fileId !== file.fileId && /\.(docx|pptx|xlsx)$/i.test(f.fileName || ''));
        setSourceCandidates(editable);
      } catch (err: any) {
        if (!cancelled) setSourceCandidatesError('Could not load candidate source files');
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeaders, file.fileId]);

  // 2026-08-18 — audience x channel is enforced server-side
  // (sourceValidation.audienceChannelError) but was invisible until submit, so a
  // producer filled the whole form before learning the combination is illegal.
  // Fredrik asked for it up front. Deliberately NOT solved by filtering the
  // audience list: hiding "Internal" when Portal is selected would leave someone
  // with confidential content no truthful option, and the likely result is
  // mislabelling it as Public just to get past the form. Warn and block instead —
  // the rule has a safe alternative (Content Hub) and the user needs to learn it.
  useEffect(() => {
    let cancelled = false;
    if (!audience || !channel) { setAudienceChannelWarning(null); return; }
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/content-review/taxonomy`, { headers });
        if (cancelled) return;
        const opts: any[] = res.data || [];
        const norm = (s: string) => String(s || '').trim().toLowerCase();
        const chanOpt = opts.find(o => o.kind === 'channel' && norm(o.label) === norm(channel));
        const audOpt = opts.find(o => o.kind === 'audience' && norm(o.label) === norm(audience));
        // Mirror of backend FIXED_CHANNEL_ACCESS. Only PRIVATE is incompatible,
        // and an audience with no mapped accessType is unknowable — never block,
        // exactly as the server does.
        const fixedAccess = chanOpt?.postApprovalAction === 'portal_content' ? 'PUBLIC_NOT_INDEXABLE' : null;
        if (fixedAccess && audOpt?.accessType === 'PRIVATE') {
          setAudienceChannelWarning(
            `"${audOpt.label}" content can't be published to the ${chanOpt.label} channel. ` +
            `${chanOpt.label} publishes every file as ${fixedAccess}, which would make this item ` +
            `reachable by anyone holding the link — portal page permissions don't protect the file ` +
            `URL itself. Use Content Hub, which applies the restricted access level this audience requires.`
          );
        } else {
          setAudienceChannelWarning(null);
        }
      } catch {
        // Best-effort only. The server remains the authority — a failed lookup
        // must never block a submission that the backend would accept.
        if (!cancelled) setAudienceChannelWarning(null);
      }
    })();
    return () => { cancelled = true; };
  }, [audience, channel, getAuthHeaders]);

  const submitOnce = async (acknowledgeContentHashDuplicate: boolean) => {
    const headers = await getAuthHeaders();
    // 14e-A — source fields: mutually-non-exclusive at the schema layer,
    // but the UI presents "link" and "no source w/ reason" as either/or so
    // producers don't try to do both. Backend re-validates via
    // sourceValidation.validateSourceCapture — do not skip on the client.
    const sourcePayload = noSourceChecked
      ? { noEditableSourceReason: noSourceReason || null }
      : sourceFileId
        ? { sourceFileId, sourceFileName }
        : sourceLink.trim()
          ? { sourceLink: sourceLink.trim() }
          : {};
    await axios.post(`${backendUrl}/api/content-review/submit-file`, {
      fileId: file.fileId,
      originalFileName: file.fileName,
      purpose,
      channel,
      audience,
      topic,
      geography,
      title: title.trim(),
      publicName: publicName.trim() || undefined,
      version: version.trim(),
      notes: notes.trim(),
      producer: producerId ? { teamMemberId: producerId } : undefined,
      ...sourcePayload,
      ...(acknowledgeContentHashDuplicate ? { acknowledgeContentHashDuplicate: true } : {})
    }, { headers });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); setErrorCode(null); setSuggestion(null); setHashConflict(null);

    if (!purpose || !channel || !audience || !topic || !title.trim() || !version.trim()) {
      setError('Type, Channel, Audience, Topic, Title, and Version are required.');
      return;
    }

    // 14e-A client-side gate — D81 as walk-revised (2026-08-13): pdf,
    // image, and video need a source link OR audited exception reason on
    // ANY channel (their editable original lives outside the submitted
    // file). Backend re-enforces; this just spares a round-trip.
    const isSourceRequired = detectedFormat !== null && ['pdf', 'image', 'video'].includes(detectedFormat);
    if (isSourceRequired) {
      const hasLink = sourceLink.trim().length > 0;
      const hasReason = noSourceChecked && noSourceReason;
      const hasFile = sourceFileId.length > 0;
      if (!hasFile && !hasLink && !hasReason) {
        setError(`Editable source is required for ${detectedFormat} submissions. Attach a working file (.docx / .pptx / .xlsx), add a source link (Drive / SharePoint / Figma / vm:), or tick "No editable source" with a reason.`);
        return;
      }
    }

    try {
      setSubmitting(true);
      await submitOnce(false);
      onSubmitted();
    } catch (err: any) {
      const data = err.response?.data;
      setError(data?.error || err.message || 'Submit failed');
      setErrorCode(data?.code || null);
      setSuggestion(data?.suggestion || null);
      if (data?.code === 'DUPLICATE_CONTENT_HASH') {
        setHashConflict(data?.conflict || null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAnyway = async () => {
    setError(null); setErrorCode(null); setHashConflict(null);
    try {
      setSubmitting(true);
      await submitOnce(true);
      onSubmitted();
    } catch (err: any) {
      const data = err.response?.data;
      setError(data?.error || err.message || 'Submit failed');
      setErrorCode(data?.code || null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="review-modal-overlay" onClick={onClose}>
      <div className="review-modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: '#1a1a1a', wordBreak: 'break-all' }}>Submit "{file.fileName}"</h3>
        {prefill && (
          <div role="note" style={{ background: '#E1EBF5', borderRadius: 4, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>
            <strong>Update of "{prefill.title}"</strong> — the identity fields are prefilled from the approved
            version so the history stays on one thread. Change them only if this is genuinely different content.
          </div>
        )}
        <p style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
          The file will be renamed in SharePoint to:{' '}
          <code style={{ fontSize: 11, color: '#1a1a1a', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
            {(purpose || '<Type>')} - {(audience || '<Audience>')} - {(title || '<Title>')} - {new Date().toISOString().slice(0, 10).replace(/-/g, '')} - V{(version || 'X.Y')}
          </code>
        </p>

        <form onSubmit={handleSubmit}>
          {/* WS2 (D75) — Type + Channel from the taxonomy registry with
              inline "+ Add new…". Format is auto-detected from the file
              and shown read-only. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="review-form-group">
              <label>Type</label>
              <TaxonomySelect
                kind="type"
                value={purpose}
                onChange={setPurpose}
                getAuthHeaders={getAuthHeaders}
              />
            </div>
            <div className="review-form-group">
              <label>
                Channel{' '}
                <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                  (where this goes after approval)
                </span>
              </label>
              <TaxonomySelect
                kind="channel"
                value={channel}
                onChange={setChannel}
                getAuthHeaders={getAuthHeaders}
                filterFormat={detectedFormat}
              />
              {/* 2026-08-18 — the list is filtered by format, so channels that
                  can't take this file simply vanish. Fredrik hit that during the
                  prod E2E run and could not tell a rule from a bug ("Content Hub
                  not available as option"). A hidden option costs the user the
                  reason; say it out loud. */}
              {detectedFormat && (
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  Only channels that accept <strong>.{detectedFormat}</strong> are listed.
                  {['docx', 'pptx', 'xlsx'].includes(detectedFormat) &&
                    ' Working-file formats are not distributables — export as PDF to publish to Content Hub or Portal.'}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="review-form-group">
              <label>Audience</label>
              {/* Walk finding #6 — audiences registry-fed + extensible. */}
              <TaxonomySelect
                kind="audience"
                value={audience}
                onChange={setAudience}
                getAuthHeaders={getAuthHeaders}
              />
              {audienceChannelWarning && (
                <div style={{
                  fontSize: 12, color: '#8a1c1c', marginTop: 6,
                  background: '#fdeaea', border: '1px solid #f0b5b5',
                  borderRadius: 6, padding: '8px 10px'
                }}>
                  {audienceChannelWarning}
                </div>
              )}
            </div>
            <div className="review-form-group">
              <label>Format <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>(auto-detected)</span></label>
              <input
                value={(file.fileName.match(/\.([^./]+)$/)?.[1] || 'unknown').toUpperCase()}
                readOnly
                disabled
                style={{ background: '#f3f4f6', color: '#666' }}
              />
            </div>
          </div>

          {/* v2 (D90/D94) — the two new lineage dimensions. Topic mirrors
              the portal nav categories and is REQUIRED (pick "Other" for
              edge cases); Geography defaults to Global. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="review-form-group">
              <label>
                Topic{' '}
                <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                  (portal category — pick "Other" if none fits)
                </span>
              </label>
              <TaxonomySelect
                kind="topic"
                value={topic}
                onChange={setTopic}
                getAuthHeaders={getAuthHeaders}
                placeholder="Select topic..."
              />
            </div>
            <div className="review-form-group">
              <label>Geography</label>
              <TaxonomySelect
                kind="geography"
                value={geography}
                onChange={setGeography}
                getAuthHeaders={getAuthHeaders}
                placeholder="Global"
              />
            </div>
          </div>

          <div className="review-form-group">
            <label>Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="MSCs Facts Sheet" />
          </div>

          <div className="review-form-group">
            <label>
              Public name{' '}
              <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                (optional — filename shown to external users on HubSpot; defaults to the Title)
              </span>
            </label>
            <input value={publicName} onChange={e => setPublicName(e.target.value)} placeholder={title.trim() || 'Defaults to Title'} />
          </div>

          <div className="review-form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Version{' '}
              <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                (X.Y — e.g. 0.1)
              </span>
              {/* Walk station 3 (Fredrik) — versioning explainer. CSS-only
                  hover tooltip; scheme per D2 + the approval-anchored
                  refinement in submissionService.suggestNextVersion. */}
              <span
                tabIndex={0}
                aria-label="Versioning help"
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: '#e8f0fe',
                  color: '#1a58c2',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'help'
                }}
                onMouseEnter={e => {
                  const tip = e.currentTarget.querySelector('.version-tip') as HTMLElement;
                  if (tip) tip.style.display = 'block';
                }}
                onMouseLeave={e => {
                  const tip = e.currentTarget.querySelector('.version-tip') as HTMLElement;
                  if (tip) tip.style.display = 'none';
                }}
              >
                ?
                <span
                  className="version-tip"
                  style={{
                    display: 'none',
                    position: 'absolute',
                    bottom: 22,
                    left: -10,
                    zIndex: 50,
                    width: 320,
                    padding: '10px 12px',
                    background: '#1a1a1a',
                    color: '#f3f4f6',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: 1.5,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                    whiteSpace: 'normal',
                    cursor: 'default'
                  }}
                >
                  <strong>How versioning works</strong>
                  <br />• Drafts are X.Y with Y &gt; 0 — <em>.0 is reserved for approvals</em>.
                  <br />• <strong>First-time submission:</strong> start at <code>0.1</code>. Further review rounds before approval: <code>0.2</code>, <code>0.3</code>…
                  <br />• <strong>On approval</strong> the system stamps the archive copy <code>V1.0</code> automatically — you never type a .0.
                  <br />• <strong>Updating an approved item:</strong> the major = number of approvals so far. After V1.0, the next draft is <code>1.1</code> (iterating toward V2.0); after V2.0, drafts start at <code>2.1</code>.
                  <br />• A rejected draft stays in its major: <code>0.1</code> rejected → resubmit as <code>0.2</code>.
                  <br />The form suggests the right next version automatically when it recognizes the lineage (same Type + Audience + Title).
                </span>
              </span>
            </label>
            <input
              value={version}
              onChange={e => { setVersion(e.target.value); setVersionTouched(true); }}
              placeholder="0.1"
              pattern="\d+\.\d+"
              style={{ maxWidth: 140 }}
            />
            {versionHint && (
              <div style={{ fontSize: 11, color: '#1a58c2', marginTop: 4 }}>
                {versionHint}
              </div>
            )}
          </div>

          <div className="review-form-group">
            <label>
              Producer{' '}
              <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                (defaults to you; pick another team member if submitting on their behalf)
              </span>
            </label>
            <select value={producerId} onChange={e => setProducerId(e.target.value)}>
              {team.length === 0 && <option value="">Loading team...</option>}
              {team.map(m => (
                <option key={m._id} value={m._id}>
                  {m.name}{user?.email && m.email?.toLowerCase() === user.email.toLowerCase() ? ' (you)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* 14e-A — editable source capture, D81 as walk-revised
              (2026-08-13): required for pdf/image/video on ANY channel
              (the editable original lives outside the submitted file);
              docx/pptx are their own source (optional link). This modal
              only handles file submissions; webpage cycles route through
              SubmitForReviewModal and are excluded per D87. */}
          {(() => {
            const isSourceRequired = detectedFormat !== null && ['pdf', 'image', 'video'].includes(detectedFormat);
            // 2026-08-18 — was `docx || pptx`, silently omitting xlsx even though
            // sourceValidation.EDITABLE_EXTENSIONS has been ['docx','pptx','xlsx']
            // all along, so a spreadsheet was described as needing a source it is.
            const isNativelyEditable = detectedFormat !== null && ['docx', 'pptx', 'xlsx'].includes(detectedFormat);
            return (
              <div className="review-form-group" style={{
                marginTop: 4,
                paddingTop: 12,
                borderTop: '1px solid #eee'
              }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                  Editable source
                  {isSourceRequired && (
                    <span style={{ color: '#c73535', marginLeft: 4 }} title="Required — the editable original lives outside this file">*</span>
                  )}{' '}
                  <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                    ({isSourceRequired
                      ? 'required — where does the editable source live?'
                      : isNativelyEditable
                        ? 'this file is its own source; a link is optional'
                        : 'optional — where does the design/project source live?'})
                  </span>
                </label>
                {/* Working-file picker. Listed FIRST because it is the
                    preferred answer under D81 as Fredrik framed it on
                    2026-08-18: a PDF goes out, its .pptx/.docx stays behind as
                    the editable master, and only this branch actually COPIES
                    that master into the working-files tree. A link records a
                    pointer and copies nothing. */}
                <select
                  value={sourceFileId}
                  onChange={e => {
                    const id = e.target.value;
                    const hit = (sourceCandidates || []).find(c => c.fileId === id);
                    setSourceFileId(id);
                    setSourceFileName(hit ? hit.fileName : '');
                    if (id) { setSourceLink(''); setNoSourceChecked(false); setNoSourceReason(''); }
                  }}
                  disabled={noSourceChecked || sourceLink.trim().length > 0}
                  style={{
                    width: '100%', marginBottom: 8,
                    opacity: (noSourceChecked || sourceLink.trim().length > 0) ? 0.5 : 1
                  }}
                >
                  <option value="">
                    {sourceCandidates === null
                      ? 'Loading working files in Ready to Review...'
                      : sourceCandidates.length === 0
                        ? 'No .docx / .pptx / .xlsx waiting in Ready to Review'
                        : 'Attach a working file from Ready to Review...'}
                  </option>
                  {(sourceCandidates || []).map(c => (
                    <option key={c.fileId} value={c.fileId}>{c.fileName}</option>
                  ))}
                </select>
                {sourceCandidatesError && (
                  <div style={{ fontSize: 12, color: '#b8860b', marginBottom: 8 }}>{sourceCandidatesError}</div>
                )}
                {sourceFileId && !isNativelyEditable && (
                  <div style={{ fontSize: 12, color: '#1e7e34', marginBottom: 8 }}>
                    ✓ <strong>{sourceFileName}</strong> will be copied into Approved content - working files on approval.
                  </div>
                )}
                {/* 2026-08-18, found during the prod E2E run: a companion attached to
                    a file that is ALREADY its own editable master silently wins —
                    workingFilesService.resolveSource checks the companion branch
                    before the primary one — so the wrong document gets archived as
                    this content's working master, under this content's lineage, with
                    nothing to indicate it. Fredrik hit exactly this by attaching a
                    file to a .docx "to see what happens". Say so, loudly. */}
                {sourceFileId && isNativelyEditable && (
                  <div style={{
                    fontSize: 12, color: '#8a5a00', marginBottom: 8,
                    background: '#fff8ee', border: '1px solid #f0d9b5',
                    borderRadius: 6, padding: '8px 10px'
                  }}>
                    ⚠️ This <strong>.{detectedFormat}</strong> is already its own editable master, so it
                    does not need a working file. <strong>{sourceFileName}</strong> would be copied into
                    Approved content - working files <em>instead of</em> the file you are submitting.
                    {' '}Clear this unless that is genuinely what you want.
                    <button
                      type="button"
                      onClick={() => { setSourceFileId(''); setSourceFileName(''); }}
                      style={{
                        display: 'block', marginTop: 6, fontSize: 12, cursor: 'pointer',
                        background: '#fff', border: '1px solid #d0b48a', borderRadius: 4, padding: '3px 10px'
                      }}
                    >
                      Clear working file
                    </button>
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#999', margin: '0 0 6px' }}>
                  {sourceFileId ? 'Or, instead of a file — ' : 'Or link to a source held elsewhere:'}
                </div>
                <input
                  type="text"
                  value={sourceLink}
                  onChange={e => { setSourceLink(e.target.value); if (e.target.value) { setNoSourceChecked(false); setNoSourceReason(''); setSourceFileId(''); setSourceFileName(''); } }}
                  placeholder={detectedFormat === 'video'
                    ? 'https://frame.io/… or vm:render-vm/projects/promo.aep'
                    : detectedFormat === 'image'
                      ? 'https://figma.com/file/…'
                      : 'https://cellcoab.sharepoint.com/…/draft.docx  or  https://drive.google.com/…'}
                  disabled={noSourceChecked}
                  style={{ opacity: noSourceChecked ? 0.5 : 1 }}
                />
                {/* Walk station 3 design pass: the exception affordance sits
                    in its own quiet box (was a floating checkbox that
                    aligned oddly against the input above); checkbox row and
                    reason select share the box's grid so everything lines
                    up regardless of state. */}
                {isSourceRequired && (
                  <div style={{
                    marginTop: 10,
                    padding: '10px 12px',
                    background: noSourceChecked ? '#fff8ee' : '#fafafa',
                    border: `1px solid ${noSourceChecked ? '#f0d9b5' : '#eee'}`,
                    borderRadius: 8,
                    fontSize: 13,
                    transition: 'all 0.15s ease'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontWeight: 500,
                      color: '#444',
                      cursor: 'pointer',
                      margin: 0
                    }}>
                      <input
                        type="checkbox"
                        checked={noSourceChecked}
                        onChange={e => {
                          setNoSourceChecked(e.target.checked);
                          if (e.target.checked) { setSourceLink(''); setSourceFileId(''); setSourceFileName(''); }
                          else setNoSourceReason('');
                        }}
                        style={{ width: 15, height: 15, margin: 0, flexShrink: 0 }}
                      />
                      No editable source available
                      <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>
                        (audited exception)
                      </span>
                    </label>
                    {noSourceChecked && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginTop: 8,
                        paddingLeft: 23
                      }}>
                        <span style={{ fontSize: 12, color: '#8a6d3b', flexShrink: 0 }}>Reason</span>
                        <select
                          value={noSourceReason}
                          onChange={e => setNoSourceReason(e.target.value as any)}
                          style={{ flex: '0 1 280px', padding: '5px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}
                        >
                          <option value="">Select a reason…</option>
                          <option value="legacy">Legacy content (predates working-files policy)</option>
                          <option value="scanned">Scanned / OCR only</option>
                          <option value="third-party">Third-party file (no source available)</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="review-form-group">
            <label>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Context for reviewers — what changed, what to focus on..."
              rows={2}
            />
          </div>

          {error && (
            <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 12 }}>
              {error}
              {errorCode === 'DUPLICATE_VERSION' && suggestion && (
                <div style={{ marginTop: 6 }}>
                  <button
                    type="button"
                    className="review-btn review-btn-outline"
                    onClick={() => { setVersion(suggestion); setError(null); setErrorCode(null); setSuggestion(null); }}
                    style={{ fontSize: 12 }}
                  >
                    Use suggested version {suggestion}
                  </button>
                </div>
              )}
              {errorCode === 'DUPLICATE_CONTENT_HASH' && hashConflict && (
                <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="review-btn review-btn-outline"
                    onClick={handleSubmitAnyway}
                    disabled={submitting}
                    style={{ fontSize: 12 }}
                  >
                    {submitting ? 'Submitting…' : 'Submit anyway'}
                  </button>
                  <span style={{ fontSize: 11, color: '#888' }}>
                    Matches: "{hashConflict.name || 'untitled'}" v{hashConflict.version || '?'} ({hashConflict.status})
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="review-action-bar">
            <button
              type="submit"
              className="review-btn review-btn-primary"
              disabled={submitting || audienceChannelWarning !== null}
              title={audienceChannelWarning || undefined}
            >
              {submitting ? 'Submitting...' : 'Submit for Review'}
            </button>
            <button type="button" className="review-btn review-btn-outline" onClick={onBack}>Back</button>
            <button type="button" className="review-btn review-btn-outline" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewDashboard;
